import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  PieChart, Pie, Cell,
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import { supabase } from '../supabase';
// Shared working-days calc (weekends + Peru holidays excluded). Imported under
// an alias because this file keeps a separate calendar-day daysBetween() used
// for project-duration estimates.
import { phaseMetrics } from '../utils/phaseMetrics';
import useBreakpoint from '../hooks/useBreakpoint';
import { tap, kpiGridMobile, pageMobile, TAP_TARGET } from '../mobileStyles';

// másquemuros brand palette — 10 distinct colors used across all charts.
const PALETTE = [
  '#6B1C2A', // wine dark
  '#C97B32', // gold
  '#4A1219', // dark burgundy
  '#8B6914', // dark gold
  '#3E2A1A', // dark brown
  '#2A3E1A', // dark olive
  '#1C3E6B', // dark blue
  '#6B2A5C', // dark purple
  '#C4A882', // light beige
  '#8B2E3F', // medium wine
];
const pickColor = (i) => PALETTE[i % PALETTE.length];

// Pie label renderer for the Origen chart. Anchors text outside the slice
// with a label-line, stacks "<Channel>\n<NN%>" on two lines, and hides
// the label entirely for tiny slices (<4%) to prevent overlap.
function renderOrigenLabel(props) {
  const { cx, cy, midAngle, outerRadius, name, fill } = props;
  // The data row in `origenData` already stores `percent` as an integer in
  // the 0-100 range (computed once in the memo via clients.length/total*100).
  // Read it through payload and don't multiply again — recharts may also
  // surface its own 0-1 `percent` in props, which would compound into 4200%.
  const row = props.payload || {};
  const pct = typeof row.percent === 'number'
    ? row.percent
    : Math.round((props.percent || 0) * 100);
  if (pct < 4) return null;
  const RAD = Math.PI / 180;
  const r = outerRadius + 14;
  const x = cx + r * Math.cos(-midAngle * RAD);
  const y = cy + r * Math.sin(-midAngle * RAD);
  const anchor = x > cx ? 'start' : 'end';
  const short = (name || '').length > 14 ? `${name.slice(0, 13)}…` : name;
  return (
    <text x={x} y={y} textAnchor={anchor} dominantBaseline="central" fontFamily="'DM Sans', sans-serif">
      <tspan x={x} dy="-0.55em" fontSize={11} fontWeight={600} fill="#2D2D2D">{short}</tspan>
      <tspan x={x} dy="1.15em" fontSize={11} fontWeight={700} fill={fill}>{pct}%</tspan>
    </text>
  );
}
function renderOrigenLabelLine(props) {
  const { points, stroke } = props;
  const row = props.payload || {};
  const pct = typeof row.percent === 'number'
    ? row.percent
    : Math.round((props.percent || 0) * 100);
  if (pct < 4 || !points || points.length < 2) return null;
  const [a, b] = points;
  return (
    <polyline
      points={`${a.x},${a.y} ${b.x},${b.y}`}
      stroke={stroke || 'rgba(45,45,45,0.35)'}
      strokeWidth={1}
      fill="none"
    />
  );
}

// Shared tooltip card style used by both Pie and Bar custom tooltips.
const TOOLTIP_CARD = {
  background: '#fff',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '10px 14px',
  fontSize: 12,
  fontFamily: "'DM Sans', sans-serif",
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  color: 'var(--text-primary)',
  maxWidth: 320,
};

// Tooltip for the Estado bar chart — status name + project count + the list
// of project names that share that status.
function BarTooltip({ active, payload }) {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0];
  const row = d.payload || {};
  const name = row.name ?? d.name ?? '—';
  const value = row.value ?? d.value ?? 0;
  const projects = Array.isArray(row.projects) ? row.projects : [];
  return (
    <div style={TOOLTIP_CARD}>
      <div style={{ fontWeight: 700, color: 'var(--wine)', marginBottom: projects.length ? 6 : 0 }}>
        {name} — {value} {value === 1 ? 'proyecto' : 'proyectos'}
      </div>
      {projects.length > 0 && (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 3 }}>
          {projects.map((p, i) => (
            <li key={i} style={{ fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.45 }}>
              <span style={{ color: 'var(--wine)', marginRight: 6 }}>•</span>
              {p}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Explanatory text rendered inside the per-section info tooltips.
const INFO = {
  eficiencia:
    "Solo entran proyectos con la fase marcada como Completada. El tiempo efectivo suma " +
    "únicamente los días con tareas activas, fusionando las tareas que se solapan. El tiempo " +
    "total va del primer al último día e incluye las pausas.\n" +
    "Para la métrica de días totales de diseño se excluyen los proyectos con más de 10 días " +
    "muertos acumulados, ya que reflejan demoras del cliente y no del estudio.",
  origen:
    "Se obtiene del campo 'Origen de contacto' de cada proyecto en el Dashboard.\n" +
    "Si un proyecto no tiene origen asignado, aparece como 'Sin definir'.",
  estado:
    "Se obtiene del campo 'Estado' de cada proyecto (En curso, Pendiente, Pausado, Completado).\n" +
    "La duración se calcula entre la fecha de inicio y fecha de entrega estimada.\n" +
    "'Con retraso' son proyectos cuya fecha estimada ya pasó y no están completados.",
  equipo:
    "Solo incluye al equipo interno: se excluyen Ximena y los proveedores.\n" +
    "'Proyectos simultáneos' = proyectos distintos trabajados en el mes seleccionado (todas las tareas si eliges 'Todos los meses').\n" +
    "'Promedio proyectos/mes' = combinaciones distintas (mes + proyecto) / meses con actividad.\n" +
    "'M² asignados' proviene de los proyectos donde el miembro es arquitecta responsable (no cambia con el mes).\n" +
    "Una tarea cuenta en un mes si su inicio o su fin caen en ese mes.",
  productividad:
    "Tareas al 100%: tareas que llegaron al 100% de progreso, agrupadas por mes según su fecha de fin.\n" +
    "Entradas en Journal: entradas escritas por el equipo en Mi Journal, agrupadas por mes.\n" +
    "Muestra los últimos 6 meses.",
  clientes:
    "Se obtiene del campo 'Cliente' de cada proyecto.\n" +
    "'Recurrentes' son clientes que aparecen en más de un proyecto.",
};

// Cumulative dead days a diseño phase may accumulate before it stops counting
// towards the "días totales" average.
const DEAD_DAYS_LIMIT = 10;

// Detail table columns — every one of them is sortable.
const EF_COLUMNS = [
  { key: 'nombre',        label: 'Proyecto',        align: 'left' },
  { key: 'metraje',       label: 'm²',              align: 'right' },
  { key: 'fase',          label: 'Fase',            align: 'left' },
  { key: 'efectivo',      label: 'Días efectivos',  align: 'right' },
  { key: 'total',         label: 'Días totales',    align: 'right' },
  { key: 'muertos',       label: 'Días muertos',    align: 'right' },
  { key: 'efectivoPerM2', label: 'Efectivo/m²',     align: 'right' },
  { key: 'totalPerM2',    label: 'Total/m²',        align: 'right' },
  { key: 'entraATotal',   label: '¿Entra a total?', align: 'center' },
];

const MONTH_NAMES_SHORT = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const MONTH_NAMES_LONG = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

// Rendimiento del Equipo only counts internal staff. Ximena (studio lead) and
// proveedores (external suppliers) are excluded from the productivity table.
function isExcludedTeamName(nombre) {
  const n = (nombre || '').trim().toLowerCase();
  return n === 'ximena' || n === 'ximena li' || n.split(' ')[0] === 'ximena';
}
function isProveedorCargo(cargo) {
  const c = (cargo || '').toLowerCase();
  return c.includes('proveedor') || c.includes('especialidad') || c.includes('especialista');
}
function isInternalMember(m) {
  return !isExcludedTeamName(m.nombre) && !isProveedorCargo(m.cargo);
}

function ymdToDate(s) {
  if (!s) return null;
  const d = new Date(String(s).slice(0, 10) + 'T00:00:00');
  return Number.isNaN(d.getTime()) ? null : d;
}
function daysBetween(a, b) {
  if (!a || !b) return null;
  return Math.round((b.getTime() - a.getTime()) / 86_400_000) + 1;
}

function formatDuration(days) {
  if (days == null || !Number.isFinite(days) || days <= 0) return '—';
  const total = Math.round(days);
  const months = Math.floor(total / 30);
  const rem = total - months * 30;
  if (months === 0) return `${rem} día${rem === 1 ? '' : 's'}`;
  if (rem === 0) return `${months} mes${months === 1 ? '' : 'es'}`;
  return `${months} mes${months === 1 ? '' : 'es'} y ${rem} día${rem === 1 ? '' : 's'}`;
}

function formatPerM2(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toFixed(2);
}
// Summing metraje floats drifts (151.16000000000001) — round every accumulated
// m² figure to one decimal before it reaches the UI.
function round1(n) {
  if (n == null || !Number.isFinite(Number(n))) return 0;
  return Math.round(Number(n) * 10) / 10;
}
function monthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function monthLabel(d) {
  return `${MONTH_NAMES_SHORT[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
}
function monthLabelLong(d) {
  return `${MONTH_NAMES_LONG[d.getMonth()]} ${d.getFullYear()}`;
}
function lastNMonths(n) {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth() - (n - 1), 1);
  const out = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
    out.push({ key: monthKey(d), label: monthLabel(d), labelLong: monthLabelLong(d), date: d });
  }
  return out;
}
// The month keys a task "touches" — the month of its inicio and the month of
// its fin. A task counts toward a month if either falls in it.
function taskMonthKeys(t) {
  const keys = new Set();
  const di = ymdToDate(t.inicio); if (di) keys.add(monthKey(di));
  const df = ymdToDate(t.fin);    if (df) keys.add(monthKey(df));
  return Array.from(keys);
}

export default function Estadisticas({ onNavigate } = {}) {
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [team, setTeam] = useState([]);
  const [delayedOpen, setDelayedOpen] = useState(false);
  const [selectedOrigen, setSelectedOrigen] = useState(null); // channel name or null
  const [teamSort, setTeamSort] = useState('tareas'); // 'tareas' | 'm2'
  const [teamMonth, setTeamMonth] = useState('all'); // 'all' | monthKey ('YYYY-MM')
  const [teamExpanded, setTeamExpanded] = useState(() => new Set()); // member names with the project list open
  const [monthlyMetric, setMonthlyMetric] = useState('proyectos'); // 'proyectos' | 'm2'
  const [monthDetail, setMonthDetail] = useState(null); // monthKey of the bar clicked, or null
  const { isMobile, isTablet, isCompact } = useBreakpoint();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [pRes, tRes, eRes] = await Promise.all([
      supabase.from('proyectos').select('id, nombre, cliente, cliente_origen, estado, estado_diseno, estado_obra, inicio, fin_estimado, metraje, arquitecta_id, arquitecta_nombre, created_at'),
      supabase.from('tareas').select('id, nombre, asignado, progreso, proyecto_id, tipo, inicio, fin, created_at'),
      supabase.from('equipo').select('id, nombre, color, cargo'),
    ]);
    if (pRes.data) setProjects(pRes.data);
    if (tRes.data) setTasks(tRes.data);
    if (eRes.data) setTeam(eRes.data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Section 1: Eficiencia por Metraje ──────────────────────────────────
  // One row per project-phase. A phase only counts once the project marks it
  // Completada (estado_diseno / estado_obra), so half-finished work never
  // drags the averages down.
  const phaseRows = useMemo(() => {
    const tasksByProject = new Map();
    for (const t of tasks) {
      if (!t.proyecto_id) continue;
      if (!tasksByProject.has(t.proyecto_id)) tasksByProject.set(t.proyecto_id, []);
      tasksByProject.get(t.proyecto_id).push(t);
    }
    const rows = [];
    for (const p of projects) {
      const metraje = Number(p.metraje);
      if (!Number.isFinite(metraje) || metraje <= 0) continue;
      const ptasks = tasksByProject.get(p.id) || [];
      // Tasks with no tipo are treated as obra, matching the Gantt default.
      const phases = [
        {
          fase: 'Diseño',
          completa: p.estado_diseno === 'Completado',
          tasks: ptasks.filter(t => (t.tipo || 'obra') === 'diseño'),
        },
        {
          fase: 'Obra',
          completa: p.estado_obra === 'Completado',
          tasks: ptasks.filter(t => (t.tipo || 'obra') === 'obra'),
        },
      ];
      for (const ph of phases) {
        if (!ph.completa || ph.tasks.length === 0) continue;
        const m = phaseMetrics(ph.tasks);
        if (m.total <= 0) continue; // phase has tasks, but none with usable dates
        rows.push({
          key: `${p.id}:${ph.fase}`,
          projectId: p.id,
          nombre: p.nombre || 'Sin nombre',
          metraje,
          fase: ph.fase,
          efectivo: m.efectivo,
          total: m.total,
          muertos: m.muertos,
          efectivoPerM2: m.efectivo / metraje,
          totalPerM2: m.total / metraje,
          // Dead days in diseño are the client's waiting time, not the studio's
          // pace, so they're kept out of the "días totales" average. In obra the
          // studio sets the rhythm, so every completed obra counts.
          entraATotal: ph.fase === 'Obra' || m.muertos <= DEAD_DAYS_LIMIT,
        });
      }
    }
    return rows;
  }, [projects, tasks]);

  const eficiencia = useMemo(() => {
    const avg = (arr, key) => (arr.length ? arr.reduce((s, r) => s + r[key], 0) / arr.length : null);
    const names = arr => arr.map(r => r.nombre);

    const diseno = phaseRows.filter(r => r.fase === 'Diseño');
    const obra = phaseRows.filter(r => r.fase === 'Obra');
    const disenoTotales = diseno.filter(r => r.entraATotal);
    const disenoExcluidos = diseno.filter(r => !r.entraATotal);

    // "Proyecto completo" needs BOTH phases completed; the two durations are
    // summed before dividing by the metraje.
    const byProject = new Map();
    for (const r of phaseRows) {
      if (!byProject.has(r.projectId)) {
        byProject.set(r.projectId, { nombre: r.nombre, metraje: r.metraje, fases: {} });
      }
      byProject.get(r.projectId).fases[r.fase] = r;
    }
    const completos = [...byProject.values()]
      .filter(p => p.fases['Diseño'] && p.fases['Obra'])
      .map(p => ({
        nombre: p.nombre,
        efectivoPerM2: (p.fases['Diseño'].efectivo + p.fases['Obra'].efectivo) / p.metraje,
        totalPerM2: (p.fases['Diseño'].total + p.fases['Obra'].total) / p.metraje,
      }));

    return {
      efectivoDiseno: { value: avg(diseno, 'efectivoPerM2'), projects: names(diseno) },
      totalDiseno: {
        value: avg(disenoTotales, 'totalPerM2'),
        projects: names(disenoTotales),
        excluded: disenoExcluidos.map(r => ({ nombre: r.nombre, muertos: r.muertos })),
      },
      efectivoObra: { value: avg(obra, 'efectivoPerM2'), projects: names(obra) },
      totalObra: { value: avg(obra, 'totalPerM2'), projects: names(obra) },
      completo: {
        value: avg(completos, 'totalPerM2'),
        efectivo: avg(completos, 'efectivoPerM2'),
        projects: completos.map(c => c.nombre),
      },
      analizados: { value: byProject.size, projects: [...byProject.values()].map(p => p.nombre) },
    };
  }, [phaseRows]);

  const [efSort, setEfSort] = useState({ key: 'nombre', dir: 'asc' });
  const sortedPhaseRows = useMemo(() => {
    const { key, dir } = efSort;
    const mult = dir === 'asc' ? 1 : -1;
    return [...phaseRows].sort((a, b) => {
      const x = a[key];
      const y = b[key];
      if (typeof x === 'string') return mult * x.localeCompare(y, 'es');
      if (typeof x === 'boolean') return mult * ((x === y) ? 0 : (x ? 1 : -1));
      return mult * ((x || 0) - (y || 0));
    });
  }, [phaseRows, efSort]);

  function toggleEfSort(key) {
    setEfSort(cur => (cur.key === key
      ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'nombre' || key === 'fase' ? 'asc' : 'desc' }));
  }

  const [calcMetraje, setCalcMetraje] = useState('');
  const [calcModalidad, setCalcModalidad] = useState('Ambos');
  const [calcMetric, setCalcMetric] = useState('total'); // 'total' | 'efectivo'

  const calcResult = useMemo(() => {
    const m = parseFloat(calcMetraje);
    if (!Number.isFinite(m) || m <= 0) return null;
    const useTotal = calcMetric === 'total';
    const perM2 = {
      Diseño: useTotal ? eficiencia.totalDiseno.value : eficiencia.efectivoDiseno.value,
      Obra:   useTotal ? eficiencia.totalObra.value   : eficiencia.efectivoObra.value,
      Ambos:  useTotal ? eficiencia.completo.value    : eficiencia.completo.efectivo,
    };
    const sample = {
      Diseño: useTotal ? eficiencia.totalDiseno.projects.length : eficiencia.efectivoDiseno.projects.length,
      Obra:   useTotal ? eficiencia.totalObra.projects.length   : eficiencia.efectivoObra.projects.length,
      Ambos:  eficiencia.completo.projects.length,
    };
    const dis = perM2.Diseño != null ? perM2.Diseño * m : null;
    const obr = perM2.Obra != null ? perM2.Obra * m : null;
    if (calcModalidad === 'Diseño') return { total: dis, diseno: dis, obra: null, sample: sample.Diseño };
    if (calcModalidad === 'Obra')   return { total: obr, diseno: null, obra: obr, sample: sample.Obra };
    return {
      total: perM2.Ambos != null ? perM2.Ambos * m : null,
      diseno: dis,
      obra: obr,
      sample: sample.Ambos,
    };
  }, [calcMetraje, calcModalidad, calcMetric, eficiencia]);

  // ── Section 2: Origen de Clientes ──────────────────────────────────────
  // Group projects by cliente_origen and attach the client/project pairs
  // so the pie tooltip can list "Maria Paz Izquierdo (La Floresta)" etc.
  const origenData = useMemo(() => {
    const groups = new Map();
    for (const p of projects) {
      const key = (p.cliente_origen || '').trim() || 'Otro';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push({
        cliente: (p.cliente || '').trim() || 'Sin nombre',
        proyecto: (p.nombre || '').trim() || null,
      });
    }
    const total = projects.length;
    const rows = Array.from(groups.entries()).map(([name, clients]) => ({
      name,
      value: clients.length,
      percent: total ? Math.round((clients.length / total) * 100) : 0,
      clients: clients.sort((a, b) => a.cliente.localeCompare(b.cliente)),
    }));
    return rows
      .sort((a, b) => b.value - a.value)
      .map((entry, i) => ({ ...entry, color: pickColor(i) }));
  }, [projects]);
  const origenTotal = origenData.reduce((a, b) => a + b.value, 0);

  // Cap the donut at 6 slices. Anything past that collapses into a single
  // "Otros" wedge that carries every grouped channel's clients so the
  // tooltip still lists them. The side list keeps the full breakdown.
  const ORIGEN_PIE_LIMIT = 6;
  const origenChartData = useMemo(() => {
    if (origenData.length <= ORIGEN_PIE_LIMIT) return origenData;
    const top = origenData.slice(0, ORIGEN_PIE_LIMIT - 1);
    const rest = origenData.slice(ORIGEN_PIE_LIMIT - 1);
    const otros = {
      name: 'Otros',
      value: rest.reduce((sum, r) => sum + r.value, 0),
      percent: rest.reduce((sum, r) => sum + r.percent, 0),
      clients: rest.flatMap(r => r.clients || []),
      groupedChannels: rest.map(r => ({ name: r.name, value: r.value })),
      color: '#9B8B7A',
    };
    return [...top, otros];
  }, [origenData]);

  // ── Section 2: Estado de Proyectos ─────────────────────────────────────
  // Labels match the exact DB values stored on `proyectos.estado` so the
  // chart axis, tooltip header and underlying filter all line up.
  const estadoData = useMemo(() => {
    const labels = ['En curso', 'Pendiente', 'Pausado', 'Completado'];
    const groups = Object.fromEntries(labels.map(l => [l, []]));
    for (const p of projects) {
      if (groups[p.estado]) groups[p.estado].push((p.nombre || 'Sin nombre').trim() || 'Sin nombre');
    }
    return labels.map((name, i) => ({
      name,
      value: groups[name].length,
      projects: groups[name].sort((a, b) => a.localeCompare(b)),
      color: pickColor(i),
    }));
  }, [projects]);

  const projectMetrics = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let totalDays = 0;
    let countedDuration = 0;
    let fastest = null;  // { name, days }
    let slowest = null;
    const delayedList = [];
    for (const p of projects) {
      const ini = ymdToDate(p.inicio);
      const fin = ymdToDate(p.fin_estimado);
      if (ini && fin) {
        const d = daysBetween(ini, fin);
        if (d != null && d > 0) {
          totalDays += d;
          countedDuration++;
          if (!fastest || d < fastest.days) fastest = { name: p.nombre, days: d };
          if (!slowest || d > slowest.days) slowest = { name: p.nombre, days: d };
        }
      }
      if (fin && fin < today && p.estado !== 'Completado') {
        const overdueMs = today.getTime() - fin.getTime();
        const daysOverdue = Math.max(1, Math.round(overdueMs / 86400000));
        delayedList.push({
          id: p.id,
          nombre: p.nombre,
          cliente: p.cliente || '',
          fin_estimado: p.fin_estimado,
          estado: p.estado || '—',
          daysOverdue,
        });
      }
    }
    delayedList.sort((a, b) => b.daysOverdue - a.daysOverdue);
    const avgDays = countedDuration ? totalDays / countedDuration : 0;
    return {
      total: projects.length,
      avgMonths: avgDays ? (avgDays / 30).toFixed(1) : '0',
      fastest,
      slowest,
      delayed: delayedList.length,
      delayedList,
    };
  }, [projects]);

  const delayedTasksByProject = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const projectIndex = new Map(projects.map(p => [p.id, p]));
    const groups = new Map(); // proyecto_id → { project, tasks: [] }
    for (const t of tasks) {
      const fin = ymdToDate(t.fin);
      const prog = Number(t.progreso) || 0;
      if (!fin || fin >= today || prog >= 100) continue;
      const proj = projectIndex.get(t.proyecto_id);
      if (!groups.has(t.proyecto_id)) {
        groups.set(t.proyecto_id, {
          projectId: t.proyecto_id,
          projectName: proj?.nombre || '(sin proyecto)',
          projectEstado: proj?.estado || null,
          tasks: [],
        });
      }
      const overdueMs = today.getTime() - fin.getTime();
      groups.get(t.proyecto_id).tasks.push({
        id: t.id,
        nombre: t.nombre || '(sin nombre)',
        asignado: t.asignado || '—',
        fin: t.fin,
        progreso: prog,
        daysOverdue: Math.max(1, Math.round(overdueMs / 86400000)),
      });
    }
    const list = Array.from(groups.values());
    list.forEach(g => g.tasks.sort((a, b) => b.daysOverdue - a.daysOverdue));
    list.sort((a, b) => b.tasks.length - a.tasks.length);
    return list;
  }, [tasks, projects]);

  const totalDelayedTasks = useMemo(
    () => delayedTasksByProject.reduce((sum, g) => sum + g.tasks.length, 0),
    [delayedTasksByProject],
  );

  function toggleTeamMember(nombre) {
    setTeamExpanded(prev => {
      const next = new Set(prev);
      if (next.has(nombre)) next.delete(nombre); else next.add(nombre);
      return next;
    });
  }

  function openProjectGantt(projectId) {
    if (!projectId || !onNavigate) return;
    onNavigate('gantt', { projectId });
  }

  // ── Section 3: Rendimiento del Equipo ──────────────────────────────────
  // Month options for the filter: "Todos los meses" + the last 12 months,
  // most-recent first (e.g. "Junio 2026", "Mayo 2026", …).
  const teamMonthOptions = useMemo(() => lastNMonths(12).slice().reverse(), []);

  const teamPerformance = useMemo(() => {
    // Only internal equipo members — Ximena and proveedores are excluded, and
    // we never create buckets for unknown assignees/architects.
    const members = team.filter(isInternalMember);
    const projectIndex = new Map(projects.map(p => [p.id, p]));
    const byName = new Map();
    for (const m of members) {
      byName.set(m.nombre, {
        nombre: m.nombre,
        color: m.color || '#6B1C2A',
        m2: 0,
        projectsCount: 0,
        monthProjects: new Map(), // monthKey → Set(proyecto_id) across ALL months
        selProjects: new Set(),   // distinct proyecto_id in the selected month (or all)
        selTasks: 0,              // task count in the selected month (or all)
        selDetail: new Map(),     // proyecto_id → { projectName, tasks: [] } for the expandable row
      });
    }
    for (const t of tasks) {
      if (!t.asignado) continue;
      // Match assignee text to an internal member: exact name, then first-name
      // prefix for the common "FirstName L." style. Skip anyone unmatched.
      let key = byName.has(t.asignado) ? t.asignado : null;
      if (!key) {
        for (const m of members) {
          if (t.asignado.startsWith(m.nombre.split(' ')[0])) { key = m.nombre; break; }
        }
      }
      if (!key) continue;
      const bucket = byName.get(key);
      const months = taskMonthKeys(t);
      const pid = t.proyecto_id;
      // Cross-month accumulation feeds the "promedio proyectos/mes" average.
      for (const mk of months) {
        if (!bucket.monthProjects.has(mk)) bucket.monthProjects.set(mk, new Set());
        if (pid != null) bucket.monthProjects.get(mk).add(pid);
      }
      // Selected-month (or all) accumulation feeds the visible columns.
      const inSelected = teamMonth === 'all' || months.includes(teamMonth);
      if (inSelected) {
        bucket.selTasks += 1;
        if (pid != null) {
          bucket.selProjects.add(pid);
          if (!bucket.selDetail.has(pid)) {
            bucket.selDetail.set(pid, {
              projectId: pid,
              projectName: projectIndex.get(pid)?.nombre || '(sin proyecto)',
              tasks: [],
            });
          }
          bucket.selDetail.get(pid).tasks.push({
            id: t.id,
            nombre: t.nombre || '(sin nombre)',
            inicio: t.inicio || '',
          });
        }
      }
    }
    // m² + project count from proyectos.arquitecta_nombre (kept as-is). Only
    // attributed to internal members; non-equipo architects are ignored here.
    for (const p of projects) {
      const arq = (p.arquitecta_nombre || '').trim();
      if (!arq) continue;
      const bucket = byName.get(arq);
      if (!bucket) continue;
      const m2 = Number(p.metraje);
      if (Number.isFinite(m2) && m2 > 0) bucket.m2 += m2;
      bucket.projectsCount += 1;
    }
    return Array.from(byName.values()).map(b => {
      const activeMonths = b.monthProjects.size;
      let comboCount = 0; // distinct (month + proyecto) combinations across all months
      for (const set of b.monthProjects.values()) comboCount += set.size;
      const m2 = Math.round(b.m2 * 10) / 10;
      // Expandable breakdown: projects A→Z, tasks chronological within each.
      const detalle = Array.from(b.selDetail.values())
        .map(g => ({
          ...g,
          tasks: [...g.tasks].sort((x, y) => (x.inicio || '').localeCompare(y.inicio || '')),
        }))
        .sort((x, y) => x.projectName.localeCompare(y.projectName, 'es'));
      return {
        nombre: b.nombre,
        color: b.color,
        m2,
        projectsCount: b.projectsCount,
        simultaneos: b.selProjects.size,
        detalle,
        tareas: b.selTasks,
        activeMonths,
        avgProjectsPerMonth: activeMonths ? Math.round((comboCount / activeMonths) * 10) / 10 : 0,
        m2PerMonth: activeMonths ? Math.round(m2 / activeMonths) : 0,
      };
    });
  }, [tasks, team, projects, teamMonth]);

  const teamPerformanceSorted = useMemo(() => {
    const list = [...teamPerformance];
    if (teamSort === 'm2') {
      list.sort((a, b) => (b.m2 - a.m2) || (b.simultaneos - a.simultaneos));
    } else {
      list.sort((a, b) => (b.simultaneos - a.simultaneos) || (b.tareas - a.tareas) || (b.m2 - a.m2));
    }
    return list;
  }, [teamPerformance, teamSort]);

  // "Valeria: 130 m² en 1 proyecto vs Patricia: 19 m² en 3 proyectos" —
  // contrast the highest-m² member against the lowest-m²-with-projects one
  // to call out density imbalances in the team's workload.
  const teamM2Note = useMemo(() => {
    const withM2 = teamPerformance.filter(m => m.m2 > 0);
    if (withM2.length < 2) return null;
    const max = withM2.reduce((best, m) => m.m2 > best.m2 ? m : best, withM2[0]);
    const min = withM2.reduce((worst, m) => m.m2 < worst.m2 ? m : worst, withM2[0]);
    if (max.nombre === min.nombre) return null;
    const fmt = (m) => `${m.nombre.split(' ')[0]}: ${m.m2} m² en ${m.projectsCount} proyecto${m.projectsCount === 1 ? '' : 's'}`;
    return `${fmt(max)} vs ${fmt(min)}`;
  }, [teamPerformance]);

  // ── Section 4: Productividad Mensual ───────────────────────────────────
  // 12-month series of project starts (count) and total m² started, plus
  // the auto-derived "temporada alta/baja" classification per month.
  const monthlyProductivity = useMemo(() => {
    const months = lastNMonths(12);
    const projByMonth = {};
    const m2ByMonth = {};
    const listByMonth = {}; // monthKey → projects started that month (detail panel)
    for (const p of projects) {
      const d = ymdToDate(p.inicio);
      if (!d) continue;
      const k = monthKey(d);
      projByMonth[k] = (projByMonth[k] || 0) + 1;
      const m2 = Number(p.metraje);
      const hasM2 = Number.isFinite(m2) && m2 > 0;
      if (hasM2) m2ByMonth[k] = (m2ByMonth[k] || 0) + m2;
      if (!listByMonth[k]) listByMonth[k] = [];
      listByMonth[k].push({
        id: p.id,
        nombre: p.nombre || '(sin nombre)',
        cliente: (p.cliente || '').trim(),
        m2: hasM2 ? round1(m2) : null,
      });
    }
    // Biggest projects first, then A→Z, so the detail panel leads with the
    // month's heaviest contributors.
    for (const k of Object.keys(listByMonth)) {
      listByMonth[k].sort((a, b) => (b.m2 || 0) - (a.m2 || 0) || a.nombre.localeCompare(b.nombre, 'es'));
    }
    const rows = months.map(m => ({
      key: m.key,
      month: m.label,
      monthLong: m.labelLong,
      proyectos: projByMonth[m.key] || 0,
      m2: round1(m2ByMonth[m.key] || 0),
      projectList: listByMonth[m.key] || [],
    }));
    const totalProj = rows.reduce((s, r) => s + r.proyectos, 0);
    const totalM2 = rows.reduce((s, r) => s + r.m2, 0);
    const avgProj = rows.length ? totalProj / rows.length : 0;
    const avgM2 = rows.length ? totalM2 / rows.length : 0;
    // Threshold tolerance — within ±15% of the mean reads as "normal".
    const classify = (value, avg) => {
      if (avg === 0) return 'normal';
      const ratio = value / avg;
      if (ratio >= 1.15) return 'alta';
      if (ratio <= 0.85) return 'baja';
      return 'normal';
    };
    const annotated = rows.map(r => ({
      ...r,
      seasonProj: classify(r.proyectos, avgProj),
      seasonM2: classify(r.m2, avgM2),
    }));
    const topByProj = annotated.reduce((best, r) => (!best || r.proyectos > best.proyectos) ? r : best, null);
    const topByM2 = annotated.reduce((best, r) => (!best || r.m2 > best.m2) ? r : best, null);
    return {
      rows: annotated,
      avgProj: round1(avgProj),
      avgM2: round1(avgM2),
      topByProj,
      topByM2,
    };
  }, [projects]);

  const monthDetailRow = useMemo(() => (
    monthDetail ? monthlyProductivity.rows.find(r => r.key === monthDetail) || null : null
  ), [monthDetail, monthlyProductivity]);

  // ── Section 5: Clientes ────────────────────────────────────────────────
  const clientStats = useMemo(() => {
    const byClient = new Map();
    for (const p of projects) {
      const name = (p.cliente || '').trim();
      if (!name) continue;
      byClient.set(name, (byClient.get(name) || 0) + 1);
    }
    const sorted = Array.from(byClient.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
    const top = sorted.slice(0, 5);
    const repeat = sorted.filter(c => c.count > 1);
    const topOrigen = origenData[0] || null;
    return { top, repeat, totalClients: byClient.size, topOrigen };
  }, [projects, origenData]);

  if (loading) {
    return (
      <div style={isMobile ? { ...s.container, ...pageMobile } : isTablet ? { ...s.container, ...s.containerTablet } : s.container}>
        <Header />
        <SkeletonGrid />
      </div>
    );
  }

  return (
    <div style={isMobile ? { ...s.container, ...pageMobile } : isTablet ? { ...s.container, ...s.containerTablet } : s.container}>
      <style>{`
        .recharts-wrapper,
        .recharts-wrapper:focus,
        .recharts-wrapper *:focus,
        .recharts-surface,
        .recharts-surface:focus {
          outline: none !important;
          -webkit-tap-highlight-color: transparent;
        }
        .mqm-wine-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(107,28,42,0.55) rgba(107,28,42,0.08);
        }
        .mqm-wine-scroll::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .mqm-wine-scroll::-webkit-scrollbar-track {
          background: rgba(107,28,42,0.06);
          border-radius: 8px;
        }
        .mqm-wine-scroll::-webkit-scrollbar-thumb {
          background: rgba(107,28,42,0.45);
          border-radius: 8px;
        }
        .mqm-wine-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(107,28,42,0.65);
        }
      `}</style>
      <Header />

      {/* SECTION 1 — Eficiencia por Metraje */}
      <Section
        title="Eficiencia por Metraje"
        subtitle="Productividad del estudio basada en metros cuadrados trabajados"
        info={INFO.eficiencia}
      >
        {/* Calculator */}
        <div style={s.card}>
          <div style={isMobile ? { ...s.cardHeader, ...s.cardHeaderMobile } : s.cardHeader}>
            <span style={s.cardTitle}>Estimador de duración para nuevo proyecto</span>
            {calcResult?.sample > 0 && (
              <span style={s.cardBadge}>
                Basado en {calcResult.sample} proyecto{calcResult.sample === 1 ? '' : 's'} completado{calcResult.sample === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <div style={isMobile ? { ...s.calcBody, ...s.calcBodyMobile } : s.calcBody}>
            <div style={isMobile ? { ...s.calcInputs, ...s.calcInputsMobile } : s.calcInputs}>
              <label style={s.calcField}>
                <span style={s.calcLabel}>Metraje del proyecto (m²)</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={calcMetraje}
                  onChange={e => setCalcMetraje(e.target.value)}
                  placeholder="Ej. 120"
                  style={isCompact ? { ...s.calcInput, ...s.controlTouch } : s.calcInput}
                />
              </label>
              <label style={s.calcField}>
                <span style={s.calcLabel}>Tipo</span>
                <select
                  value={calcModalidad}
                  onChange={e => setCalcModalidad(e.target.value)}
                  style={isCompact ? { ...s.calcInput, ...s.controlTouch } : s.calcInput}
                >
                  <option value="Diseño">Diseño</option>
                  <option value="Obra">Obra</option>
                  <option value="Ambos">Ambos</option>
                </select>
              </label>
              <div style={s.calcField}>
                <span style={s.calcLabel}>Métrica</span>
                <div style={s.calcToggle}>
                  <button
                    type="button"
                    onClick={() => setCalcMetric('total')}
                    style={isCompact
                      ? { ...s.calcToggleBtn, ...s.calcToggleBtnTouch, ...(calcMetric === 'total' ? s.calcToggleBtnActive : {}) }
                      : { ...s.calcToggleBtn, ...(calcMetric === 'total' ? s.calcToggleBtnActive : {}) }}
                  >Tiempo total</button>
                  <button
                    type="button"
                    onClick={() => setCalcMetric('efectivo')}
                    style={isCompact
                      ? { ...s.calcToggleBtn, ...s.calcToggleBtnTouch, ...(calcMetric === 'efectivo' ? s.calcToggleBtnActive : {}) }
                      : { ...s.calcToggleBtn, ...(calcMetric === 'efectivo' ? s.calcToggleBtnActive : {}) }}
                  >Tiempo efectivo</button>
                </div>
              </div>
            </div>

            {calcResult ? (
              <div style={s.calcResults}>
                <div style={s.calcMain}>
                  <span style={s.calcMainLabel}>
                    Duración estimada — {calcMetric === 'total' ? 'tiempo total' : 'tiempo efectivo'}
                  </span>
                  <span style={s.calcMainValue}>{formatDuration(calcResult.total)}</span>
                </div>
                {calcModalidad === 'Ambos' && (calcResult.diseno != null || calcResult.obra != null) && (
                  <div style={s.calcSplit}>
                    {calcResult.diseno != null && (
                      <span>📐 Diseño: <strong>{formatDuration(calcResult.diseno)}</strong></span>
                    )}
                    {calcResult.obra != null && (
                      <span>🏗️ Obra: <strong>{formatDuration(calcResult.obra)}</strong></span>
                    )}
                  </div>
                )}
                <span style={isMobile ? { ...s.calcNote, ...s.calcNoteMobile } : s.calcNote}>
                  El tiempo total incluye la espera normal del cliente — es el más realista para
                  cotizar. El tiempo efectivo son solo los días trabajados — sirve para medir
                  capacidad del equipo.
                </span>
                <span style={isMobile ? { ...s.calcNote, ...s.calcNoteMobile } : s.calcNote}>
                  Basado en {calcResult.sample} proyecto{calcResult.sample === 1 ? '' : 's'} completado{calcResult.sample === 1 ? '' : 's'}.
                </span>
              </div>
            ) : (
              <div style={s.calcEmpty}>
                Ingresa un metraje para estimar la duración.
              </div>
            )}
          </div>
        </div>

        {/* KPIs — row 1: diseño */}
        <div style={isMobile ? { ...s.statRow, ...kpiGridMobile, marginTop: 16 } : { ...s.statRow, marginTop: 16 }}>
          <MetricCard
            label="Días efectivos por m² — Diseño"
            value={formatPerM2(eficiencia.efectivoDiseno.value)}
            hint="solo días trabajados"
            info="Promedio de días hábiles con tareas activas por m², fusionando las tareas que se solapan. Entran todos los proyectos con la fase de diseño completada."
            projects={eficiencia.efectivoDiseno.projects}
          />
          <MetricCard
            label="Días totales por m² — Diseño"
            value={formatPerM2(eficiencia.totalDiseno.value)}
            hint="incluye esperas"
            info={`Del primer al último día de diseño, pausas incluidas. Se excluyen los proyectos con más de ${DEAD_DAYS_LIMIT} días muertos acumulados, porque reflejan demoras del cliente.`}
            projects={eficiencia.totalDiseno.projects}
            excluded={eficiencia.totalDiseno.excluded}
          />
          <MetricCard
            label="Proyecto completo (Diseño + Obra)"
            value={formatPerM2(eficiencia.completo.value)}
            hint="días totales por m²"
            info="Solo proyectos con AMBAS fases completadas. Suma la duración total de diseño y de obra antes de dividir por el metraje."
            projects={eficiencia.completo.projects}
          />
        </div>

        {/* KPIs — row 2: obra */}
        <div style={isMobile ? { ...s.statRow, ...kpiGridMobile, marginTop: 12 } : { ...s.statRow, marginTop: 12 }}>
          <MetricCard
            label="Días efectivos por m² — Obra"
            value={formatPerM2(eficiencia.efectivoObra.value)}
            hint="solo días trabajados"
            info="Promedio de días hábiles con tareas activas por m² en obra, fusionando las tareas que se solapan."
            projects={eficiencia.efectivoObra.projects}
          />
          <MetricCard
            label="Días totales por m² — Obra"
            value={formatPerM2(eficiencia.totalObra.value)}
            hint="incluye esperas"
            info="Del primer al último día de obra. Aquí no se descarta ningún proyecto por días muertos: en obra el ritmo lo marca el estudio, no el cliente."
            projects={eficiencia.totalObra.projects}
          />
          <MetricCard
            label="Proyectos analizados"
            value={eficiencia.analizados.value}
            hint="con al menos una fase completada"
            info="Proyectos con metraje, tareas con fechas y al menos una fase marcada como Completada. Un proyecto con ambas fases completadas aporta dos filas al detalle."
            projects={eficiencia.analizados.projects}
          />
        </div>

        {/* Table */}
        <div style={{ ...s.card, marginTop: 16 }}>
          <div style={s.cardHeader}>
            <span style={{ ...s.cardTitle, display: 'inline-flex', alignItems: 'center' }}>
              Detalle por proyecto y fase
              <InfoTooltip
                text={`Una fila por fase completada, así que un proyecto con diseño y obra terminados aparece dos veces. Las filas en rojo quedaron fuera de la métrica de días totales por superar ${DEAD_DAYS_LIMIT} días muertos.`}
                label="Qué muestra el detalle"
              />
            </span>
            <span style={s.cardBadge}>{phaseRows.length}</span>
          </div>
          {phaseRows.length === 0 ? (
            <Empty>
              Aún no hay fases completadas. Marca “Estado de Diseño” o “Estado de Obra”
              como Completado en la ficha del proyecto para que entre al análisis.
            </Empty>
          ) : isCompact ? (
            <div>
              <div style={s.mSortBar}>
                <span style={s.sortLabel}>Ordenar por</span>
                <select
                  value={efSort.key}
                  onChange={e => toggleEfSort(e.target.value)}
                  style={{ ...s.monthSelect, ...s.controlTouch, flex: 1, minWidth: 0 }}
                  aria-label="Ordenar detalle por"
                >
                  {EF_COLUMNS.map(col => (
                    <option key={col.key} value={col.key}>{col.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => toggleEfSort(efSort.key)}
                  style={{ ...s.sortBtn, ...s.controlTouch }}
                  aria-label={efSort.dir === 'asc' ? 'Orden ascendente' : 'Orden descendente'}
                >
                  {efSort.dir === 'asc' ? '▲' : '▼'}
                </button>
              </div>
              {sortedPhaseRows.map((r, i) => (
                <div key={r.key} style={{ ...s.mRowCard, ...(i === 0 ? { borderTop: 'none' } : {}), ...(r.entraATotal ? {} : s.trExcluded) }}>
                  <div style={s.mRowCardHead}>
                    <span style={s.mRowCardTitle}>
                      {r.nombre}
                      {!r.entraATotal && <span style={s.warnIcon}>⚠️</span>}
                    </span>
                    <span style={s.mRowCardTag}>{r.fase === 'Diseño' ? '📐 Diseño' : '🏗️ Obra'}</span>
                  </div>
                  <div style={s.mKvGrid}>
                    <div style={s.mKv}><span style={s.mKvLabel}>m²</span><span style={s.mKvValue}>{r.metraje} m²</span></div>
                    <div style={s.mKv}><span style={s.mKvLabel}>Días efectivos</span><span style={{ ...s.mKvValue, fontWeight: 600 }}>{r.efectivo}</span></div>
                    <div style={s.mKv}><span style={s.mKvLabel}>Días totales</span><span style={s.mKvValue}>{r.total}</span></div>
                    <div style={s.mKv}>
                      <span style={s.mKvLabel}>Días muertos</span>
                      <span style={{
                        ...s.mKvValue,
                        color: r.muertos > DEAD_DAYS_LIMIT ? '#B83B3B' : 'var(--text-secondary)',
                        fontWeight: r.muertos > DEAD_DAYS_LIMIT ? 600 : 400,
                      }}>{r.muertos}</span>
                    </div>
                    <div style={s.mKv}><span style={s.mKvLabel}>Efectivo/m²</span><span style={{ ...s.mKvValue, fontWeight: 600, color: 'var(--wine)' }}>{formatPerM2(r.efectivoPerM2)}</span></div>
                    <div style={s.mKv}><span style={s.mKvLabel}>Total/m²</span><span style={s.mKvValue}>{formatPerM2(r.totalPerM2)}</span></div>
                    <div style={s.mKv}>
                      <span style={s.mKvLabel}>¿Entra a total?</span>
                      {r.entraATotal
                        ? <span style={{ ...s.mKvValue, color: '#4A7C59', fontWeight: 700 }}>Sí</span>
                        : <span style={{ ...s.mKvValue, color: '#B83B3B', fontWeight: 700 }}>No</span>}
                    </div>
                  </div>
                  {!r.entraATotal && (
                    <div style={{ ...s.mRowCardNote, padding: 0 }}>
                      Excluido de días totales: {r.muertos} días muertos supera el límite de {DEAD_DAYS_LIMIT}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={s.eficienciaScroll}>
              <table style={{ ...s.table, minWidth: 880 }}>
                <thead>
                  <tr>
                    {EF_COLUMNS.map(col => (
                      <th
                        key={col.key}
                        style={{ ...s.th, textAlign: col.align, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => toggleEfSort(col.key)}
                        title={`Ordenar por ${col.label}`}
                      >
                        {col.label}
                        {efSort.key === col.key && (
                          <span style={s.sortArrow}>{efSort.dir === 'asc' ? '▲' : '▼'}</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedPhaseRows.map(r => (
                    <tr
                      key={r.key}
                      style={{ ...s.tr, ...(r.entraATotal ? {} : s.trExcluded) }}
                      title={r.entraATotal
                        ? undefined
                        : `Excluido de días totales: ${r.muertos} días muertos supera el límite de ${DEAD_DAYS_LIMIT}`}
                    >
                      <td style={s.td}>
                        <span style={{ fontWeight: 600 }}>{r.nombre}</span>
                        {!r.entraATotal && <span style={s.warnIcon}>⚠️</span>}
                      </td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{r.metraje} m²</td>
                      <td style={s.td}>{r.fase === 'Diseño' ? '📐 Diseño' : '🏗️ Obra'}</td>
                      <td style={{ ...s.td, textAlign: 'right', fontWeight: 600 }}>{r.efectivo}</td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{r.total}</td>
                      <td style={{
                        ...s.td,
                        textAlign: 'right',
                        color: r.muertos > DEAD_DAYS_LIMIT ? '#B83B3B' : 'var(--text-secondary)',
                        fontWeight: r.muertos > DEAD_DAYS_LIMIT ? 600 : 400,
                      }}>
                        {r.muertos}
                      </td>
                      <td style={{ ...s.td, textAlign: 'right', fontWeight: 600, color: 'var(--wine)' }}>
                        {formatPerM2(r.efectivoPerM2)}
                      </td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{formatPerM2(r.totalPerM2)}</td>
                      <td style={{ ...s.td, textAlign: 'center' }}>
                        {r.entraATotal
                          ? <span style={{ color: '#4A7C59', fontWeight: 700 }}>Sí</span>
                          : <span style={{ color: '#B83B3B', fontWeight: 700 }}>No</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p style={isCompact ? { ...s.eficienciaNote, ...s.eficienciaNoteCompact } : s.eficienciaNote}>
            <strong>Días efectivos</strong> = días con tareas activas, sin contar dos veces las que se solapan ·{' '}
            <strong>Días totales</strong> = del primer al último día, pausas incluidas ·{' '}
            <strong>Días muertos</strong> = la diferencia entre ambos
          </p>
        </div>
      </Section>

      {/* SECTION 2 — Origen de Clientes */}
      <Section title="Origen de Clientes" subtitle="Cómo nos descubren los clientes" info={INFO.origen}>
        {origenData.length === 0 ? (
          <Empty>Aún no hay datos de origen de contacto.</Empty>
        ) : (() => {
          const activeChannel = selectedOrigen
            ? (origenData.find(o => o.name === selectedOrigen)
              || origenChartData.find(o => o.name === selectedOrigen)
              || null)
            : null;
          const otrosChannelNames = (activeChannel?.groupedChannels || []).map(g => g.name);
          const toggleChannel = (name) => setSelectedOrigen(prev => prev === name ? null : name);

          return (
            <div style={s.origenUnifiedCard}>
              <div style={s.origenUnifiedHeader}>
                <span style={s.cardTitle}>Por canal</span>
                <span style={s.cardBadge}>{origenTotal} total</span>
              </div>
              <div style={isCompact ? { ...s.origenUnifiedBody, ...s.origenUnifiedBodyCompact } : s.origenUnifiedBody}>
                {/* LEFT (45%) — donut */}
                <div
                  style={isCompact ? { ...s.origenChartCol, ...s.origenChartColCompact } : s.origenChartCol}
                  onMouseDown={e => e.preventDefault()}
                >
                  <ResponsiveContainer width="100%" height={isMobile ? 240 : 300}>
                    <PieChart style={{ outline: 'none' }} margin={isMobile ? { top: 8, right: 8, bottom: 8, left: 8 } : { top: 18, right: 30, bottom: 18, left: 30 }}>
                      <Pie
                        data={origenChartData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={isMobile ? 58 : 48}
                        outerRadius={isMobile ? 100 : 84}
                        paddingAngle={2}
                        isAnimationActive={false}
                        onClick={(d) => {
                          const name = d?.name || d?.payload?.name;
                          if (name) toggleChannel(name);
                        }}
                        label={isMobile ? false : renderOrigenLabel}
                        labelLine={isMobile ? false : renderOrigenLabelLine}
                        style={{ outline: 'none', cursor: 'pointer' }}
                      >
                        {origenChartData.map((entry) => {
                          const isActive = selectedOrigen === entry.name;
                          const isDimmed = selectedOrigen && !isActive;
                          return (
                            <Cell
                              key={entry.name}
                              fill={entry.color}
                              stroke={isActive ? '#6B1C2A' : '#fff'}
                              strokeWidth={isActive ? 3 : 1}
                              style={{
                                outline: 'none',
                                opacity: isDimmed ? 0.35 : 1,
                                transition: 'opacity 0.15s',
                              }}
                            />
                          );
                        })}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* RIGHT (55%) — always-visible channel list */}
                <div className="mqm-wine-scroll" style={isCompact ? { ...s.origenListCol, ...s.origenListColCompact } : s.origenListCol}>
                  {origenData.map(o => {
                    const isActive = selectedOrigen === o.name;
                    return (
                      <button
                        key={o.name}
                        type="button"
                        onClick={() => toggleChannel(o.name)}
                        style={{
                          ...s.origenChannelRow,
                          ...(isCompact ? s.origenChannelRowCompact : {}),
                          ...(isActive ? s.origenChannelRowActive : {}),
                        }}
                        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(107,28,42,0.03)'; }}
                        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <span style={{ ...s.colorDot, background: o.color }} />
                        <span style={isMobile ? { ...s.origenChannelName, ...s.origenChannelNameMobile } : s.origenChannelName}>{o.name}</span>
                        <span style={s.origenChannelCount}>{o.value}</span>
                        <div style={s.origenChannelBarTrack}>
                          <div style={{
                            ...s.origenChannelBarFill,
                            width: `${Math.max(2, o.percent)}%`,
                            background: o.color,
                          }} />
                        </div>
                        <span style={s.origenChannelPercent}>{o.percent}%</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Expandable projects panel — slides under the unified card */}
              {activeChannel && (
                <div style={s.origenExpand}>
                  <div style={s.origenExpandHeader}>
                    <span style={isMobile ? { display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 } : { display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ ...s.colorDot, background: activeChannel.color }} />
                      <strong style={{ color: 'var(--wine)' }}>Proyectos desde {activeChannel.name}</strong>
                      <span style={{ fontSize: 11.5, color: 'var(--text-light)' }}>
                        · {activeChannel.value} {activeChannel.value === 1 ? 'proyecto' : 'proyectos'} · {activeChannel.percent}%
                      </span>
                    </span>
                    <button type="button" style={isCompact ? { ...s.origenExpandClose, ...s.closeBtnCompact } : s.origenExpandClose} onClick={() => setSelectedOrigen(null)} aria-label="Cerrar">✕</button>
                  </div>
                  {otrosChannelNames.length > 0 && activeChannel.name === 'Otros' && (
                    <div style={s.origenExpandSub}>Agrupa: {otrosChannelNames.join(' · ')}</div>
                  )}
                  {(activeChannel.clients || []).length === 0 ? (
                    <div style={s.origenExpandEmpty}>Sin proyectos en este canal.</div>
                  ) : (
                    <ul className="mqm-wine-scroll" style={s.origenExpandList}>
                      {activeChannel.clients.map((c, i) => {
                        const project = c.proyecto;
                        const client = c.cliente || 'Sin cliente';
                        return (
                          <li key={i} style={isMobile ? { ...s.origenExpandItem, ...s.origenExpandItemMobile } : s.origenExpandItem}>
                            <span style={s.origenExpandBullet}>•</span>
                            <span style={s.origenExpandItemText}>
                              {project ? (
                                <>
                                  <strong style={{ color: 'var(--text-primary)' }}>{project}</strong>
                                  <span style={{ color: 'var(--text-secondary)' }}> — {client}</span>
                                </>
                              ) : (
                                <strong style={{ color: 'var(--text-primary)' }}>{client}</strong>
                              )}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}

            </div>
          );
        })()}
      </Section>

      {/* SECTION 3 — Estado de Proyectos */}
      <Section title="Estado de Proyectos" subtitle="Distribución y métricas de duración" info={INFO.estado}>
        <div style={isMobile ? { ...s.statRow, ...kpiGridMobile } : s.statRow}>
          <Stat label="Total proyectos"  value={projectMetrics.total} />
          <Stat label="Duración promedio" value={`${projectMetrics.avgMonths} m`} hint="meses" />
          <Stat
            label="Más rápido"
            value={projectMetrics.fastest ? `${(projectMetrics.fastest.days / 30).toFixed(1)} m` : '—'}
            hint={projectMetrics.fastest?.name || ''}
          />
          <Stat
            label="Más largo"
            value={projectMetrics.slowest ? `${(projectMetrics.slowest.days / 30).toFixed(1)} m` : '—'}
            hint={projectMetrics.slowest?.name || ''}
          />
          <Stat
            label="Con retraso"
            value={projectMetrics.delayed}
            hint={projectMetrics.delayed > 0 ? 'clic para ver detalle' : 'entrega vencida'}
            warn={projectMetrics.delayed > 0}
            onClick={projectMetrics.delayed > 0 ? () => setDelayedOpen(o => !o) : undefined}
            active={delayedOpen}
          />
        </div>

        {delayedOpen && projectMetrics.delayedList.length > 0 && (
          <div style={s.delayedPanel}>
            <div style={s.delayedHeader}>
              <strong>Proyectos con entrega vencida</strong>
              <button type="button" style={isCompact ? { ...s.delayedClose, ...s.closeBtnCompact } : s.delayedClose} onClick={() => setDelayedOpen(false)}>✕</button>
            </div>
            {isCompact ? (
              <div>
                {projectMetrics.delayedList.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    style={s.mDelayedCard}
                    onClick={() => openProjectGantt(p.id)}
                    title="Abrir Gantt del proyecto"
                  >
                    <div style={s.mRowCardHead}>
                      <span style={s.mRowCardTitle}>{p.nombre}</span>
                      <span style={s.mDelayedDays}>
                        {p.daysOverdue} {p.daysOverdue === 1 ? 'día' : 'días'}
                      </span>
                    </div>
                    <div style={s.mKvGrid}>
                      <div style={s.mKv}><span style={s.mKvLabel}>Cliente</span><span style={s.mKvValue}>{p.cliente || '—'}</span></div>
                      <div style={s.mKv}><span style={s.mKvLabel}>Entrega estimada</span><span style={s.mKvValue}>{p.fin_estimado || '—'}</span></div>
                      <div style={s.mKv}><span style={s.mKvLabel}>Estado</span><span style={s.mKvValue}>{p.estado}</span></div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
            <div style={s.delayedTableWrap}>
              <table style={s.delayedTable}>
                <thead>
                  <tr>
                    <th style={s.delayedTh}>Proyecto</th>
                    <th style={s.delayedTh}>Cliente</th>
                    <th style={s.delayedTh}>Entrega estimada</th>
                    <th style={{ ...s.delayedTh, textAlign: 'right' }}>Días de retraso</th>
                    <th style={s.delayedTh}>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {projectMetrics.delayedList.map(p => (
                    <tr
                      key={p.id}
                      style={s.delayedRow}
                      onClick={() => openProjectGantt(p.id)}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(107,28,42,0.04)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      title="Abrir Gantt del proyecto"
                    >
                      <td style={s.delayedTd}><strong>{p.nombre}</strong></td>
                      <td style={s.delayedTd}>{p.cliente || '—'}</td>
                      <td style={s.delayedTd}>{p.fin_estimado || '—'}</td>
                      <td style={{ ...s.delayedTd, textAlign: 'right', color: '#B83B3B', fontWeight: 700 }}>
                        {p.daysOverdue} {p.daysOverdue === 1 ? 'día' : 'días'}
                      </td>
                      <td style={s.delayedTd}>{p.estado}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
        )}

        <div
          style={isMobile ? { ...s.card, padding: 12, height: 240, marginTop: 16, outline: 'none' } : { ...s.card, padding: 16, height: 300, marginTop: 16, outline: 'none' }}
          onMouseDown={e => e.preventDefault()}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={estadoData} margin={{ top: 8, right: 16, left: -10, bottom: 0 }} style={{ outline: 'none' }}>
              <CartesianGrid stroke="#f0ebe3" vertical={false} />
              <XAxis dataKey="name" stroke="#999" fontSize={12} />
              <YAxis allowDecimals={false} stroke="#999" fontSize={12} />
              <Tooltip cursor={{ fill: 'rgba(107,28,42,0.04)' }} content={<BarTooltip />} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} style={{ outline: 'none' }}>
                {estadoData.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} style={{ outline: 'none' }} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={s.delayedTasksWrap}>
          <div style={s.delayedTasksHeader}>
            <h4 style={s.delayedTasksTitle}>Tareas en retraso</h4>
            <span style={s.delayedTasksCount}>
              {totalDelayedTasks} {totalDelayedTasks === 1 ? 'tarea' : 'tareas'} en {delayedTasksByProject.length} {delayedTasksByProject.length === 1 ? 'proyecto' : 'proyectos'}
            </span>
          </div>
          {delayedTasksByProject.length === 0 ? (
            <Empty>Sin tareas vencidas pendientes. 🎉</Empty>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {delayedTasksByProject.map(g => (
                <div key={g.projectId || 'sin-proyecto'} style={s.delayedGroup}>
                  <button
                    type="button"
                    style={isCompact ? { ...s.delayedGroupHeader, ...s.delayedGroupHeaderCompact } : s.delayedGroupHeader}
                    onClick={() => openProjectGantt(g.projectId)}
                    title={g.projectId ? 'Abrir Gantt del proyecto' : ''}
                  >
                    <span style={s.delayedGroupName}>{g.projectName}</span>
                    <span style={s.delayedGroupBadge}>
                      {g.tasks.length} {g.tasks.length === 1 ? 'tarea' : 'tareas'}
                    </span>
                  </button>
                  <div style={s.delayedTaskList}>
                    {g.tasks.map(t => (
                      <div key={t.id} style={s.delayedTaskRow}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={isMobile ? { ...s.delayedTaskName, ...s.delayedTaskNameMobile } : s.delayedTaskName}>{t.nombre}</div>
                          <div style={isMobile ? { ...s.delayedTaskMeta, ...s.delayedTaskMetaMobile } : s.delayedTaskMeta}>
                            {t.asignado} · vence {t.fin} · {t.progreso}% completada
                          </div>
                        </div>
                        <span style={s.delayedTaskDays}>
                          {t.daysOverdue} {t.daysOverdue === 1 ? 'día' : 'días'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* SECTION 4 — Rendimiento del Equipo */}
      <Section title="Rendimiento del Equipo" subtitle="Proyectos simultáneos, promedio mensual y m² asignados por miembro (solo equipo interno)" info={INFO.equipo}>
        {teamPerformance.length === 0 ? (
          <Empty>Aún no hay miembros del equipo con actividad.</Empty>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 12, flexWrap: 'wrap' }}>
              <div style={isMobile ? { ...s.sortGroup, ...s.sortGroupMobile } : s.sortGroup}>
                <span style={s.sortLabel}>Mes</span>
                <select
                  value={teamMonth}
                  onChange={e => setTeamMonth(e.target.value)}
                  style={isMobile ? { ...s.monthSelect, ...s.controlTouch, flex: 1, minWidth: 0 } : isCompact ? { ...s.monthSelect, ...s.controlTouch } : s.monthSelect}
                >
                  <option value="all">Todos los meses</option>
                  {teamMonthOptions.map(m => (
                    <option key={m.key} value={m.key}>{m.labelLong}</option>
                  ))}
                </select>
              </div>
              <div style={isMobile ? { ...s.sortGroup, ...s.sortGroupMobile } : s.sortGroup}>
                <span style={s.sortLabel}>Ordenar por</span>
                <button
                  type="button"
                  onClick={() => setTeamSort('tareas')}
                  style={isCompact
                    ? { ...s.sortBtn, ...s.sortBtnCompact, ...(isMobile ? { flex: 1 } : {}), ...(teamSort === 'tareas' ? s.sortBtnActive : {}) }
                    : { ...s.sortBtn, ...(teamSort === 'tareas' ? s.sortBtnActive : {}) }}
                >Proyectos</button>
                <button
                  type="button"
                  onClick={() => setTeamSort('m2')}
                  style={isCompact
                    ? { ...s.sortBtn, ...s.sortBtnCompact, ...(isMobile ? { flex: 1 } : {}), ...(teamSort === 'm2' ? s.sortBtnActive : {}) }
                    : { ...s.sortBtn, ...(teamSort === 'm2' ? s.sortBtnActive : {}) }}
                >M²</button>
              </div>
            </div>
            {teamM2Note && (
              <div style={{ ...s.teamNote, marginBottom: 10 }}>💡 {teamM2Note}</div>
            )}
            {isCompact ? (
            <div style={s.card}>
              {teamPerformanceSorted.map((m, i) => {
                const expandable = m.detalle.length > 0;
                const expanded = expandable && teamExpanded.has(m.nombre);
                return (
                  <div key={m.nombre} style={i === 0 ? { ...s.mRowCard, borderTop: 'none' } : s.mRowCard}>
                    <div style={s.mTeamHead}>
                      {expandable ? (
                        <button
                          type="button"
                          onClick={() => toggleTeamMember(m.nombre)}
                          style={{ ...s.teamToggle, ...s.closeBtnCompact, fontSize: 11 }}
                          aria-expanded={expanded}
                          aria-label={`${expanded ? 'Ocultar' : 'Ver'} proyectos de ${m.nombre}`}
                        >
                          {expanded ? '▼' : '▶'}
                        </button>
                      ) : (
                        <span style={{ width: 24, flexShrink: 0 }} />
                      )}
                      <span style={{ ...s.colorDot, background: m.color }} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                        <span style={s.mRowCardTitle}>{m.nombre}</span>
                        {m.activeMonths > 0 && m.avgProjectsPerMonth > 0 && (
                          <span style={{ ...s.teamCaption, fontSize: 13 }}>
                            {m.nombre.split(' ')[0]} puede manejar ~{m.avgProjectsPerMonth} proyecto{m.avgProjectsPerMonth === 1 ? '' : 's'} simultáneos con ~{m.m2PerMonth} m² por mes en promedio
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={s.mKvGrid}>
                      <div style={s.mKv}>
                        <span style={s.mKvLabel}>Proyectos simultáneos</span>
                        <span style={{ ...s.mKvValue, fontWeight: 600 }}>{m.simultaneos > 0 ? m.simultaneos : '—'}</span>
                      </div>
                      <div style={s.mKv}>
                        <span style={s.mKvLabel}>Promedio proyectos/mes</span>
                        <span style={s.mKvValue}>{m.avgProjectsPerMonth > 0 ? m.avgProjectsPerMonth : '—'}</span>
                      </div>
                      <div style={s.mKv}>
                        <span style={s.mKvLabel}>M² asignados</span>
                        <span style={{ ...s.mKvValue, fontWeight: 600, color: 'var(--wine)' }}>
                          {m.m2 > 0 ? `${m.m2} m²` : '—'}
                          {m.projectsCount > 0 && (
                            <span style={{ fontSize: 12, color: 'var(--text-light)', marginLeft: 4 }}>
                              · {m.projectsCount} proy.
                            </span>
                          )}
                        </span>
                      </div>
                      <div style={s.mKv}>
                        <span style={s.mKvLabel}>Tareas</span>
                        <span style={s.mKvValue}>{m.tareas > 0 ? m.tareas : '—'}</span>
                      </div>
                    </div>
                    {expanded && (
                      <div style={s.mTeamDetail}>
                        {m.detalle.map(g => (
                          <div key={g.projectId} style={s.teamDetailRow}>
                            <span style={s.teamDetailProject}>📐 {g.projectName}</span>
                            <span style={s.teamDetailDash}> — </span>
                            <span style={s.teamDetailTasks}>
                              {g.tasks.map(t => t.nombre).join(', ')}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <div style={s.mRowCardNote}>
                Proyectos simultáneos: proyectos con al menos una tarea asignada en el período seleccionado
              </div>
            </div>
            ) : (
            <div style={s.card}>
              <div style={s.tableWrap}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Miembro</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>
                        Proyectos simultáneos
                        <span style={s.thNote}>
                          Proyectos con al menos una tarea asignada en el período seleccionado
                        </span>
                      </th>
                      <th style={{ ...s.th, textAlign: 'right' }}>Promedio proyectos/mes</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>M² asignados</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>Tareas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamPerformanceSorted.map(m => {
                      const expandable = m.detalle.length > 0;
                      const expanded = expandable && teamExpanded.has(m.nombre);
                      return (
                        <React.Fragment key={m.nombre}>
                          <tr style={s.tr}>
                            <td style={s.td}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                {expandable ? (
                                  <button
                                    type="button"
                                    onClick={() => toggleTeamMember(m.nombre)}
                                    style={s.teamToggle}
                                    aria-expanded={expanded}
                                    aria-label={`${expanded ? 'Ocultar' : 'Ver'} proyectos de ${m.nombre}`}
                                    title={expanded ? 'Ocultar proyectos' : 'Ver proyectos y tareas'}
                                  >
                                    {expanded ? '▼' : '▶'}
                                  </button>
                                ) : (
                                  <span style={s.teamToggleSpacer} />
                                )}
                                <span style={{ ...s.colorDot, background: m.color }} />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  <span style={{ fontWeight: 600 }}>{m.nombre}</span>
                                  {m.activeMonths > 0 && m.avgProjectsPerMonth > 0 && (
                                    <span style={s.teamCaption}>
                                      {m.nombre.split(' ')[0]} puede manejar ~{m.avgProjectsPerMonth} proyecto{m.avgProjectsPerMonth === 1 ? '' : 's'} simultáneos con ~{m.m2PerMonth} m² por mes en promedio
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td style={{ ...s.td, textAlign: 'right', fontWeight: 600 }}>{m.simultaneos > 0 ? m.simultaneos : '—'}</td>
                            <td style={{ ...s.td, textAlign: 'right' }}>{m.avgProjectsPerMonth > 0 ? m.avgProjectsPerMonth : '—'}</td>
                            <td style={{ ...s.td, textAlign: 'right', fontWeight: 600, color: 'var(--wine)' }}>
                              {m.m2 > 0 ? `${m.m2} m²` : '—'}
                              {m.projectsCount > 0 && (
                                <span style={{ fontSize: 10.5, color: 'var(--text-light)', marginLeft: 4 }}>
                                  · {m.projectsCount} proy.
                                </span>
                              )}
                            </td>
                            <td style={{ ...s.td, textAlign: 'right' }}>{m.tareas > 0 ? m.tareas : '—'}</td>
                          </tr>
                          {expanded && (
                            <tr style={s.tr}>
                              <td colSpan={5} style={s.teamDetailCell}>
                                {m.detalle.map(g => (
                                  <div key={g.projectId} style={s.teamDetailRow}>
                                    <span style={s.teamDetailProject}>📐 {g.projectName}</span>
                                    <span style={s.teamDetailDash}> — </span>
                                    <span style={s.teamDetailTasks}>
                                      {g.tasks.map(t => t.nombre).join(', ')}
                                    </span>
                                  </div>
                                ))}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            )}
          </>
        )}
      </Section>

      {/* SECTION 5 — Productividad Mensual */}
      <Section
        title="Productividad Mensual"
        subtitle="Proyectos iniciados y m² contratados — últimos 12 meses"
        info={INFO.productividad}
      >
        <div style={isMobile ? { ...s.statRow, ...kpiGridMobile } : s.statRow}>
          <Stat
            label="Mes más activo (m²)"
            value={monthlyProductivity.topByM2 && monthlyProductivity.topByM2.m2 > 0 ? monthlyProductivity.topByM2.month : '—'}
            hint={monthlyProductivity.topByM2 && monthlyProductivity.topByM2.m2 > 0 ? `${monthlyProductivity.topByM2.m2} m²` : ''}
          />
          <Stat
            label="Mes más activo (proyectos)"
            value={monthlyProductivity.topByProj && monthlyProductivity.topByProj.proyectos > 0 ? monthlyProductivity.topByProj.month : '—'}
            hint={monthlyProductivity.topByProj && monthlyProductivity.topByProj.proyectos > 0 ? `${monthlyProductivity.topByProj.proyectos} proyectos` : ''}
          />
          <Stat label="Promedio m² / mes" value={`${monthlyProductivity.avgM2}`} hint="m² promedio" />
          <Stat label="Promedio proyectos / mes" value={`${monthlyProductivity.avgProj}`} hint="proyectos promedio" />
        </div>

        <div style={isMobile ? { display: 'flex', gap: 6, marginTop: 14, marginBottom: 8 } : { display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 14, marginBottom: 8 }}>
          <button
            type="button"
            onClick={() => setMonthlyMetric('proyectos')}
            style={isCompact
              ? { ...s.sortBtn, ...s.sortBtnCompact, ...(isMobile ? { flex: 1 } : {}), ...(monthlyMetric === 'proyectos' ? s.sortBtnActive : {}) }
              : { ...s.sortBtn, ...(monthlyMetric === 'proyectos' ? s.sortBtnActive : {}) }}
          >Ver por proyectos</button>
          <button
            type="button"
            onClick={() => setMonthlyMetric('m2')}
            style={isCompact
              ? { ...s.sortBtn, ...s.sortBtnCompact, ...(isMobile ? { flex: 1 } : {}), ...(monthlyMetric === 'm2' ? s.sortBtnActive : {}) }
              : { ...s.sortBtn, ...(monthlyMetric === 'm2' ? s.sortBtnActive : {}) }}
          >Ver por m²</button>
        </div>

        <div
          style={isMobile ? { ...s.card, padding: 12, height: 250, outline: 'none' } : { ...s.card, padding: 16, height: 320, outline: 'none' }}
          onMouseDown={e => e.preventDefault()}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyProductivity.rows} margin={{ top: 8, right: 16, left: -10, bottom: 0 }} style={{ outline: 'none' }}>
              <CartesianGrid stroke="#f0ebe3" vertical={false} />
              <XAxis dataKey="month" stroke="#999" fontSize={12} />
              <YAxis allowDecimals={false} stroke="#999" fontSize={12} />
              <Tooltip cursor={{ fill: 'rgba(107,28,42,0.04)' }} />
              <Bar
                dataKey={monthlyMetric === 'm2' ? 'm2' : 'proyectos'}
                radius={[6, 6, 0, 0]}
                name={monthlyMetric === 'm2' ? 'M² iniciados' : 'Proyectos iniciados'}
                onClick={(_, index) => {
                  const row = monthlyProductivity.rows[index];
                  if (!row) return;
                  setMonthDetail(cur => (cur === row.key ? null : row.key)); // second click closes
                }}
              >
                {monthlyProductivity.rows.map(r => {
                  const selected = monthDetail === r.key;
                  const base = monthlyMetric === 'm2' ? '#C9A96E' : '#6B1C2A';
                  const dark = monthlyMetric === 'm2' ? '#9A7C3F' : '#4A1220';
                  return (
                    <Cell
                      key={r.key}
                      fill={selected ? dark : base}
                      cursor="pointer"
                    />
                  );
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {monthDetailRow && (
          <div style={s.monthDetail}>
            <div style={s.monthDetailHead}>
              <h4 style={s.monthDetailTitle}>
                Proyectos iniciados en {monthDetailRow.monthLong || monthDetailRow.month}:{' '}
                {monthDetailRow.proyectos} proyecto{monthDetailRow.proyectos === 1 ? '' : 's'} · {monthDetailRow.m2} m²
              </h4>
              <button
                type="button"
                onClick={() => setMonthDetail(null)}
                style={isCompact ? { ...s.monthDetailClose, ...s.closeBtnCompact } : s.monthDetailClose}
                aria-label="Cerrar detalle del mes"
                title="Cerrar"
              >
                ✕
              </button>
            </div>
            {monthDetailRow.projectList.length === 0 ? (
              <p style={s.monthDetailEmpty}>Sin proyectos iniciados en este mes.</p>
            ) : (
              <div style={s.monthDetailPills}>
                {monthDetailRow.projectList.map(p => {
                  // When reading the chart by m², show each project's share of
                  // the month's total so the bar height is traceable.
                  const share = monthlyMetric === 'm2' && p.m2 && monthDetailRow.m2 > 0
                    ? Math.round((p.m2 / monthDetailRow.m2) * 100)
                    : null;
                  return (
                    <span key={p.id} style={s.monthDetailPill}>
                      <span style={s.monthDetailPillName}>{p.nombre}</span>
                      {p.cliente && <span style={s.monthDetailPillSep}> — {p.cliente}</span>}
                      {p.m2 != null && <span style={s.monthDetailPillSep}> — {p.m2} m²</span>}
                      {share != null && <span style={s.monthDetailPillShare}> · {share}% del mes</span>}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8, gap: 12, flexWrap: 'wrap' }}>
            <h4 style={s.seasonTitle}>Temporada por mes</h4>
            <div style={s.seasonLegend}>
              <span style={{ ...s.seasonChip, ...s.seasonAlta }}>🔥 Alta</span>
              <span style={{ ...s.seasonChip, ...s.seasonNormal }}>Normal</span>
              <span style={{ ...s.seasonChip, ...s.seasonBaja }}>❄️ Baja</span>
            </div>
          </div>
          <div style={s.seasonGrid}>
            {monthlyProductivity.rows.map(r => {
              const season = monthlyMetric === 'm2' ? r.seasonM2 : r.seasonProj;
              const value = monthlyMetric === 'm2' ? r.m2 : r.proyectos;
              const unit = monthlyMetric === 'm2' ? 'm²' : (value === 1 ? 'proy.' : 'proys.');
              const style = season === 'alta' ? s.seasonAlta : season === 'baja' ? s.seasonBaja : s.seasonNormal;
              return (
                <div key={r.key} style={{ ...s.seasonCell, ...style }}>
                  <div style={s.seasonMonth}>{r.month}</div>
                  <div style={s.seasonValue}>{value} <span style={s.seasonUnit}>{unit}</span></div>
                  <div style={s.seasonTag}>
                    {season === 'alta' ? '🔥 Alta' : season === 'baja' ? '❄️ Baja' : '—'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* SECTION 6 — Clientes */}
      <Section title="Clientes" subtitle="Recurrencia y origen más común" info={INFO.clientes}>
        <div style={isMobile ? { ...s.statRow, ...kpiGridMobile } : s.statRow}>
          <Stat label="Clientes únicos" value={clientStats.totalClients} />
          <Stat label="Clientes recurrentes" value={clientStats.repeat.length} hint="con más de 1 proyecto" />
          <Stat
            label="Origen más común"
            value={clientStats.topOrigen ? clientStats.topOrigen.name : '—'}
            hint={clientStats.topOrigen ? `${clientStats.topOrigen.value} proyectos` : ''}
          />
        </div>

        <div style={isMobile ? { ...s.twoCol, ...s.twoColMobile, marginTop: 16 } : { ...s.twoCol, marginTop: 16 }}>
          <div style={s.card}>
            <div style={s.cardHeader}>
              <span style={s.cardTitle}>Top clientes por proyectos</span>
            </div>
            {clientStats.top.length === 0 ? (
              <Empty>Aún no hay clientes registrados.</Empty>
            ) : (
              <div style={s.list}>
                {clientStats.top.map((c, i) => (
                  <div key={c.name} style={isMobile ? { ...s.listRow, ...s.listRowMobile } : s.listRow}>
                    <span style={s.rankBadge}>{i + 1}</span>
                    <span style={s.listLabel}>{c.name}</span>
                    <span style={s.listValue}>{c.count}</span>
                    <span style={s.listPercent}>{c.count === 1 ? 'proyecto' : 'proyectos'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={s.card}>
            <div style={s.cardHeader}>
              <span style={s.cardTitle}>Clientes recurrentes</span>
              <span style={s.cardBadge}>{clientStats.repeat.length}</span>
            </div>
            {clientStats.repeat.length === 0 ? (
              <Empty>Todavía no hay clientes con más de un proyecto.</Empty>
            ) : (
              <div style={s.list}>
                {clientStats.repeat.map(c => (
                  <div key={c.name} style={isMobile ? { ...s.listRow, ...s.listRowMobile } : s.listRow}>
                    <span style={{ ...s.colorDot, background: '#6B1C2A' }} />
                    <span style={s.listLabel}>{c.name}</span>
                    <span style={s.listValue}>{c.count}</span>
                    <span style={s.listPercent}>proyectos</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
}

function Header() {
  return (
    <header style={s.header}>
      <div>
        <h2 style={s.title}>Estadísticas</h2>
        <p style={s.subtitle}>Métricas y rendimiento del estudio</p>
      </div>
    </header>
  );
}

function Section({ title, subtitle, info, children }) {
  return (
    <section style={s.section}>
      <div style={s.sectionHead}>
        <h3 style={s.sectionTitle}>
          <span>{title}</span>
          {info && <InfoTooltip text={info} label={`Cómo se calcula: ${title}`} />}
        </h3>
        {subtitle && <p style={s.sectionSubtitle}>{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function InfoTooltip({ text, label }) {
  const [open, setOpen] = useState(false);
  const show = () => setOpen(true);
  const hide = () => setOpen(false);
  const { isMobile, isCompact, isTouch } = useBreakpoint();
  // Touch + compact: tap toggles; hover/focus handlers are skipped because
  // emulated mouse/focus events would fight the toggle.
  const tapMode = isCompact && isTouch;
  // Compact: the tip is positioned against the viewport so it never overflows.
  const [anchor, setAnchor] = useState(null); // { el, rect } of the button while open
  const openAt = (el) => { setAnchor({ el, rect: el.getBoundingClientRect() }); setOpen(true); };

  useEffect(() => {
    if (!open || !isCompact) return undefined;
    const close = () => setOpen(false);
    const onDown = (e) => { if (!anchor?.el || !anchor.el.contains(e.target)) setOpen(false); };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('pointerdown', onDown);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open, isCompact, anchor]);

  let compactTipStyle = null;
  if (isCompact && open && anchor) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const { rect } = anchor;
    const below = rect.bottom < vh * 0.6;
    const width = isMobile ? vw - 32 : Math.min(320, vw - 32);
    const left = isMobile ? 16 : Math.max(16, Math.min(rect.left, vw - width - 16));
    compactTipStyle = {
      ...s.infoTip,
      ...s.infoTipCompact,
      left,
      width,
      ...(below
        ? { top: rect.bottom + 6, maxHeight: vh - rect.bottom - 22 }
        : { bottom: vh - rect.top + 6, maxHeight: rect.top - 22 }),
    };
  }

  return (
    <span style={{ position: 'relative', display: 'inline-flex', marginLeft: 8 }}>
      <button
        type="button"
        aria-label={label || 'Información'}
        aria-expanded={tapMode ? open : undefined}
        onClick={tapMode ? (e => { e.stopPropagation(); if (open) hide(); else openAt(e.currentTarget); }) : undefined}
        onMouseEnter={tapMode ? undefined : isCompact ? (e => openAt(e.currentTarget)) : show}
        onMouseLeave={tapMode ? undefined : hide}
        onFocus={tapMode ? undefined : isCompact ? (e => openAt(e.currentTarget)) : show}
        onBlur={tapMode ? undefined : hide}
        style={isCompact ? s.infoBtnCompact : s.infoBtn}
      >
        {isCompact ? <span style={s.infoBtn}>i</span> : 'i'}
      </button>
      {open && (
        <div role="tooltip" style={compactTipStyle || s.infoTip}>{text}</div>
      )}
    </span>
  );
}

// A KPI card that names the projects behind its number. Ximena needs to see
// exactly which projects each average came from — and which ones were left out.
function MetricCard({ label, value, hint, info, projects = [], excluded = [] }) {
  const { isMobile } = useBreakpoint();
  return (
    <div style={isMobile ? { ...s.metricCard, ...s.kpiCardMobile } : s.metricCard}>
      <div style={s.metricHead}>
        <span style={s.statLabel}>{label}</span>
        {info && <InfoTooltip text={info} label={`Cómo se calcula: ${label}`} />}
      </div>
      <span style={isMobile ? { ...s.statValue, ...s.statValueMobile } : s.statValue}>{value}</span>
      {hint && <span style={s.statHint}>{hint}</span>}
      {projects.length > 0 ? (
        <span style={isMobile ? { ...s.metricProjects, ...s.metricTextMobile } : s.metricProjects}>
          Basado en {projects.length} proyecto{projects.length === 1 ? '' : 's'}: {projects.join(', ')}
        </span>
      ) : (
        <span style={isMobile ? { ...s.metricEmpty, ...s.metricTextMobile } : s.metricEmpty}>Sin proyectos que cumplan los criterios.</span>
      )}
      {excluded.length > 0 && (
        <span style={isMobile ? { ...s.metricExcluded, ...s.metricTextMobile } : s.metricExcluded}>
          Excluidos por &gt;{DEAD_DAYS_LIMIT} días muertos:{' '}
          {excluded.map(e => `${e.nombre} (${e.muertos} días)`).join(', ')}
        </span>
      )}
    </div>
  );
}

function Stat({ label, value, hint, warn, onClick, active, info }) {
  const clickable = typeof onClick === 'function';
  const { isMobile } = useBreakpoint();
  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onClick : undefined}
      onKeyDown={clickable ? (e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }) : undefined}
      style={{
        ...s.statCard,
        ...(isMobile ? s.kpiCardMobile : {}),
        ...(warn ? { borderColor: 'rgba(184,59,59,0.35)' } : {}),
        ...(clickable ? { cursor: 'pointer' } : {}),
        ...(active ? { background: 'rgba(184,59,59,0.06)', borderColor: 'rgba(184,59,59,0.5)' } : {}),
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={s.statLabel}>{label}</span>
        {info && <InfoTooltip text={info} label={`Qué significa: ${label}`} />}
      </div>
      <div style={isMobile ? { ...s.statValue, ...s.statValueMobile, ...(warn ? { color: '#B83B3B' } : {}) } : { ...s.statValue, ...(warn ? { color: '#B83B3B' } : {}) }}>{value}</div>
      {hint && <div style={s.statHint} title={hint}>{hint}</div>}
    </div>
  );
}

function Empty({ children }) {
  return <div style={s.empty}>{children}</div>;
}

function SkeletonGrid() {
  const blocks = [320, 220, 280, 320, 240];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {blocks.map((h, i) => (
        <div key={i} style={{ ...s.skeleton, height: h }}>
          <style>{`
            @keyframes mqm-skel-pulse {
              0%, 100% { opacity: 0.55; }
              50%      { opacity: 0.85; }
            }
          `}</style>
        </div>
      ))}
    </div>
  );
}

const s = {
  container: { padding: '32px 36px', maxWidth: 1400 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title: { fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 600, color: 'var(--wine)', margin: 0 },
  subtitle: { fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 },

  section: { marginBottom: 32 },
  sectionHead: { marginBottom: 14 },
  sectionTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 22, fontWeight: 600, color: 'var(--wine)',
    margin: 0,
    display: 'inline-flex',
    alignItems: 'center',
  },
  infoBtn: {
    width: 18, height: 18, borderRadius: '50%',
    border: '1.5px solid var(--wine)', background: '#fff',
    color: 'var(--wine)',
    fontSize: 11, fontWeight: 700, lineHeight: 1,
    fontFamily: "'DM Sans', sans-serif", fontStyle: 'italic',
    cursor: 'help', padding: 0,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  },
  infoTip: {
    position: 'absolute', top: 'calc(100% + 6px)', left: 0,
    background: '#fff', border: '1px solid var(--wine)',
    borderRadius: 8, padding: '10px 14px',
    minWidth: 220, maxWidth: 280,
    fontSize: 12, lineHeight: 1.5,
    fontFamily: "'DM Sans', sans-serif",
    color: 'var(--text-primary)',
    boxShadow: '0 10px 24px rgba(107,28,42,0.12)',
    whiteSpace: 'pre-line',
    zIndex: 50,
  },
  sectionSubtitle: { fontSize: 12, color: 'var(--text-secondary)', margin: '4px 0 0' },

  twoCol: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: 16,
  },

  card: {
    background: '#fff',
    borderRadius: 14,
    border: '1px solid var(--border)',
    overflow: 'hidden',
    minWidth: 0,
  },
  cardHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 18px',
    borderBottom: '1px solid var(--border)',
  },
  cardTitle: { fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', fontFamily: "'DM Sans', sans-serif" },
  cardBadge: {
    fontSize: 11, fontWeight: 600, color: 'var(--wine)',
    background: 'rgba(107,28,42,0.06)', padding: '4px 10px', borderRadius: 20,
  },

  list: { display: 'flex', flexDirection: 'column' },
  origenUnifiedCard: {
    background: '#fff',
    border: '1px solid var(--border)',
    borderRadius: 14,
    overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
  },
  origenUnifiedHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 18px',
    borderBottom: '1px solid #f3efe9',
    background: '#FDFBF8',
  },
  origenUnifiedBody: {
    display: 'grid',
    gridTemplateColumns: '45% 55%',
    minHeight: 320,
  },
  origenChartCol: {
    padding: '12px 16px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    borderRight: '1px solid #f3efe9',
  },
  origenListCol: {
    display: 'flex', flexDirection: 'column',
    maxHeight: 320,
    overflowY: 'auto',
    padding: '8px 6px 8px 0',
  },
  origenChannelRow: {
    display: 'grid',
    gridTemplateColumns: '14px 1fr 28px 1fr 38px',
    alignItems: 'center', gap: 10,
    width: '100%', padding: '8px 14px',
    border: 'none', background: 'transparent',
    cursor: 'pointer', textAlign: 'left',
    fontFamily: "'DM Sans', sans-serif",
    borderLeft: '3px solid transparent',
    transition: 'background 0.12s, border-color 0.12s',
  },
  origenChannelRowActive: {
    background: 'rgba(107,28,42,0.06)',
    borderLeft: '3px solid var(--wine)',
  },
  origenChannelName: {
    fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  origenChannelCount: {
    fontSize: 11, fontWeight: 700, color: 'var(--wine)',
    background: 'rgba(107,28,42,0.08)',
    padding: '2px 7px', borderRadius: 10,
    minWidth: 24, textAlign: 'center',
  },
  origenChannelBarTrack: {
    height: 6, background: '#F0EBE3',
    borderRadius: 4, overflow: 'hidden',
    width: '100%',
  },
  origenChannelBarFill: {
    height: '100%', borderRadius: 4,
    transition: 'width 0.25s',
  },
  origenChannelPercent: {
    fontSize: 11.5, fontWeight: 700, color: 'var(--text-secondary)',
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
  origenExpand: {
    padding: '12px 18px',
    borderTop: '1px solid #f3efe9',
    background: 'rgba(107,28,42,0.025)',
  },
  origenExpandHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 6, gap: 8,
  },
  origenExpandClose: {
    border: 'none', background: 'transparent',
    color: 'var(--text-light)', fontSize: 14, lineHeight: 1,
    cursor: 'pointer', padding: 4,
  },
  origenExpandSub: { fontSize: 11, color: 'var(--text-light)', marginBottom: 6 },
  origenExpandList: {
    margin: 0, padding: 0, listStyle: 'none',
    display: 'flex', flexDirection: 'column', gap: 4,
    maxHeight: 220, overflowY: 'auto',
    paddingRight: 4,
  },
  origenExpandItem: {
    display: 'flex', alignItems: 'baseline', gap: 8,
    fontSize: 13, lineHeight: 1.5,
  },
  origenExpandBullet: { color: 'var(--wine)', flexShrink: 0, fontWeight: 700 },
  origenExpandItemText: { flex: 1, minWidth: 0 },
  origenExpandEmpty: { fontSize: 12.5, color: 'var(--text-light)' },
  teamNote: {
    fontSize: 12.5, color: 'var(--text-secondary)',
    background: 'rgba(201,169,110,0.10)',
    border: '1px solid rgba(201,169,110,0.35)',
    padding: '6px 12px', borderRadius: 8,
    fontStyle: 'italic',
  },
  sortGroup: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
  },
  sortLabel: {
    fontSize: 11, color: 'var(--text-light)', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.05em',
    marginRight: 4,
  },
  sortBtn: {
    padding: '6px 12px', borderRadius: 8,
    border: '1px solid var(--border)', background: '#fff',
    color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  sortBtnActive: {
    background: 'var(--wine)', color: '#fff', borderColor: 'var(--wine)',
  },
  monthSelect: {
    padding: '6px 10px', borderRadius: 8,
    border: '1px solid var(--border)', background: '#fff',
    color: 'var(--text-primary)', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  teamCaption: {
    fontSize: 11, color: 'var(--text-light)', fontStyle: 'italic',
    lineHeight: 1.4,
  },

  seasonTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 18, fontWeight: 600, color: 'var(--wine)', margin: 0,
  },
  seasonLegend: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  seasonGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
    gap: 8,
  },
  seasonCell: {
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: '10px 12px',
    background: '#fff',
    display: 'flex', flexDirection: 'column', gap: 2,
  },
  seasonMonth: {
    fontSize: 11, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.05em',
    color: 'var(--text-secondary)',
  },
  seasonValue: {
    fontSize: 16, fontWeight: 700, color: 'var(--text-primary)',
    fontVariantNumeric: 'tabular-nums',
  },
  seasonUnit: { fontSize: 11, fontWeight: 500, color: 'var(--text-light)' },
  seasonTag: { fontSize: 11, fontWeight: 600, marginTop: 2 },
  seasonAlta: {
    background: 'rgba(184,59,59,0.08)',
    borderColor: 'rgba(184,59,59,0.35)',
    color: '#B83B3B',
  },
  seasonBaja: {
    background: 'rgba(62,101,148,0.08)',
    borderColor: 'rgba(62,101,148,0.35)',
    color: '#3E6594',
  },
  seasonNormal: {
    background: '#FDFBF8',
    borderColor: 'var(--border)',
    color: 'var(--text-secondary)',
  },
  seasonChip: {
    fontSize: 11, fontWeight: 700,
    padding: '3px 9px', borderRadius: 999,
    border: '1px solid currentColor',
  },

  listRow: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto auto',
    alignItems: 'center',
    gap: 10,
    padding: '10px 18px',
    borderTop: '1px solid #f3efe9',
    fontSize: 13,
  },
  colorDot: {
    width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
    boxShadow: '0 0 0 1.5px #fff',
  },
  rankBadge: {
    width: 22, height: 22, borderRadius: '50%',
    background: 'rgba(107,28,42,0.06)', color: '#6B1C2A',
    fontSize: 11, fontWeight: 700,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  listLabel: { color: 'var(--text-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  listValue: { fontWeight: 700, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' },
  listPercent: { fontSize: 11, color: 'var(--text-light)', fontWeight: 600 },

  statRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 14,
  },
  statCard: {
    background: '#fff',
    borderRadius: 14,
    border: '1px solid var(--border)',
    padding: '16px 18px',
    display: 'flex', flexDirection: 'column', gap: 4,
    minWidth: 0,
  },
  statLabel: {
    fontSize: 11, fontWeight: 600, color: 'var(--text-light)',
    textTransform: 'uppercase', letterSpacing: '0.06em',
  },
  statValue: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 28, fontWeight: 700, color: 'var(--text-primary)',
    lineHeight: 1.1,
  },
  statHint: {
    fontSize: 11, color: 'var(--text-secondary)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },

  metricCard: {
    background: '#fff',
    borderRadius: 14,
    border: '1px solid var(--border)',
    padding: '16px 18px',
    display: 'flex', flexDirection: 'column', gap: 4,
    minWidth: 0,
  },
  metricHead: { display: 'flex', alignItems: 'center', minWidth: 0 },
  // The project list is the whole point of the card — it wraps, never truncates.
  metricProjects: {
    marginTop: 6,
    fontSize: 10.5,
    lineHeight: 1.45,
    color: 'var(--wine)',
    wordBreak: 'break-word',
  },
  metricExcluded: {
    marginTop: 3,
    fontSize: 10.5,
    lineHeight: 1.45,
    color: 'var(--text-light)',
    wordBreak: 'break-word',
  },
  metricEmpty: {
    marginTop: 6, fontSize: 10.5, fontStyle: 'italic', color: 'var(--text-light)',
  },
  sortArrow: { marginLeft: 4, fontSize: 8, color: 'var(--wine)' },
  trExcluded: { background: 'rgba(184,59,59,0.06)' },
  warnIcon: { marginLeft: 6, fontSize: 11 },
  calcToggle: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  calcToggleBtn: {
    padding: '9px 14px',
    borderRadius: 9,
    border: '1px solid var(--border)',
    background: '#fff',
    color: 'var(--text-secondary)',
    fontSize: 12.5, fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    whiteSpace: 'nowrap',
  },
  calcToggleBtnActive: {
    background: 'var(--wine)', color: '#fff', borderColor: 'transparent',
  },

  delayedPanel: {
    background: '#fff',
    border: '1px solid rgba(184,59,59,0.25)',
    borderRadius: 12,
    marginTop: 16,
    overflow: 'hidden',
  },
  delayedHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 18px',
    background: 'rgba(184,59,59,0.05)',
    borderBottom: '1px solid rgba(184,59,59,0.15)',
    fontSize: 13, color: '#B83B3B',
  },
  delayedClose: {
    border: 'none', background: 'transparent',
    color: '#B83B3B', fontSize: 16, cursor: 'pointer', padding: 4,
  },
  delayedTableWrap: { overflowX: 'auto' },
  delayedTable: { width: '100%', borderCollapse: 'collapse' },
  delayedTh: {
    textAlign: 'left',
    padding: '10px 16px',
    fontSize: 11, fontWeight: 600, color: 'var(--text-light)',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    borderBottom: '1px solid var(--border)',
    fontFamily: "'DM Sans', sans-serif",
    background: '#FDFBF8',
    whiteSpace: 'nowrap',
  },
  delayedRow: { borderBottom: '1px solid #f3efe9', cursor: 'pointer', transition: 'background 0.12s' },
  delayedTd: {
    padding: '10px 16px',
    fontSize: 13,
    color: 'var(--text-primary)',
    fontVariantNumeric: 'tabular-nums',
  },

  delayedTasksWrap: { marginTop: 20 },
  delayedTasksHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    marginBottom: 10, flexWrap: 'wrap', gap: 8,
  },
  delayedTasksTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 18, fontWeight: 600, color: 'var(--wine)', margin: 0,
  },
  delayedTasksCount: {
    fontSize: 12, color: 'var(--text-secondary)',
  },
  delayedGroup: {
    background: '#fff', border: '1px solid var(--border)', borderRadius: 10,
    overflow: 'hidden',
  },
  delayedGroupHeader: {
    width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 14px', background: '#FDFBF8',
    border: 'none', borderBottom: '1px solid var(--border)',
    cursor: 'pointer', textAlign: 'left',
    fontFamily: "'DM Sans', sans-serif",
  },
  delayedGroupName: { fontSize: 13.5, fontWeight: 700, color: 'var(--wine)' },
  delayedGroupBadge: {
    fontSize: 11, fontWeight: 700, color: '#B83B3B',
    background: 'rgba(184,59,59,0.08)', padding: '3px 10px', borderRadius: 20,
  },
  delayedTaskList: { display: 'flex', flexDirection: 'column' },
  delayedTaskRow: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '10px 14px',
    borderBottom: '1px solid #f3efe9',
  },
  delayedTaskName: { fontSize: 13, color: 'var(--text-primary)', fontWeight: 600 },
  delayedTaskMeta: { fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2 },
  delayedTaskDays: {
    fontSize: 12, fontWeight: 700, color: '#B83B3B',
    background: 'rgba(184,59,59,0.08)', padding: '4px 10px', borderRadius: 16,
    whiteSpace: 'nowrap',
  },

  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    fontSize: 11, fontWeight: 600, color: 'var(--text-light)',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    borderBottom: '1px solid var(--border)',
    fontFamily: "'DM Sans', sans-serif",
    background: '#FDFBF8',
  },
  monthDetail: {
    marginTop: 12,
    padding: '14px 16px',
    background: 'var(--beige, #F7F3EE)',
    border: '1px solid rgba(107,28,42,0.14)',
    borderRadius: 12,
  },
  monthDetailHead: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 10,
  },
  monthDetailTitle: {
    margin: 0,
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 17,
    fontWeight: 600,
    color: 'var(--wine)',
    lineHeight: 1.3,
  },
  monthDetailClose: {
    flexShrink: 0,
    width: 24, height: 24,
    borderRadius: 6,
    border: 'none',
    background: 'rgba(107,28,42,0.08)',
    color: 'var(--wine)',
    fontSize: 12,
    lineHeight: 1,
    cursor: 'pointer',
    padding: 0,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: "'DM Sans', sans-serif",
  },
  monthDetailEmpty: {
    margin: 0,
    fontSize: 12.5,
    fontStyle: 'italic',
    color: 'var(--text-light)',
  },
  monthDetailPills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  monthDetailPill: {
    display: 'inline-flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    padding: '6px 12px',
    borderRadius: 999,
    background: 'var(--wine)',
    color: '#fff',
    fontSize: 12,
    lineHeight: 1.4,
    maxWidth: '100%',
  },
  monthDetailPillName: { fontWeight: 600 },
  monthDetailPillSep: { opacity: 0.85 },
  monthDetailPillShare: { opacity: 0.7, fontSize: 11 },

  // Sentence-case helper line under an uppercase column header.
  thNote: {
    display: 'block',
    marginTop: 3,
    maxWidth: 200,
    marginLeft: 'auto',
    fontSize: 9.5,
    fontWeight: 400,
    lineHeight: 1.35,
    letterSpacing: 0,
    textTransform: 'none',
    color: 'var(--text-light)',
    opacity: 0.85,
  },
  teamToggle: {
    width: 20, height: 20,
    flexShrink: 0,
    borderRadius: 5,
    border: 'none',
    background: 'transparent',
    color: 'var(--wine)',
    fontSize: 9,
    lineHeight: 1,
    cursor: 'pointer',
    padding: 0,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: "'DM Sans', sans-serif",
  },
  teamToggleSpacer: { width: 20, flexShrink: 0 },
  teamDetailCell: {
    padding: '10px 16px 12px 46px', // indented under the member name
    background: 'var(--beige, #F7F3EE)',
    fontSize: 12,
  },
  teamDetailRow: {
    marginBottom: 5,
    lineHeight: 1.45,
    wordBreak: 'break-word',
  },
  teamDetailProject: { color: 'var(--wine)', fontWeight: 600 },
  teamDetailDash: { color: 'var(--text-light)' },
  teamDetailTasks: { color: 'var(--text-secondary)' },
  tr: { borderBottom: '1px solid #f3efe9' },
  td: {
    padding: '12px 16px',
    fontSize: 13,
    color: 'var(--text-primary)',
    fontVariantNumeric: 'tabular-nums',
  },

  rateRow: { display: 'flex', alignItems: 'center', gap: 10 },
  rateBar: {
    flex: 1, height: 6, background: '#F0EBE3', borderRadius: 3, overflow: 'hidden',
    minWidth: 80,
  },
  rateFill: { height: '100%', borderRadius: 3, transition: 'width 0.4s ease' },
  ratePct: { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', minWidth: 40, textAlign: 'right' },

  empty: {
    padding: 24,
    textAlign: 'center',
    color: 'var(--text-light)',
    fontSize: 13,
    background: '#FDFBF8',
    border: '1px dashed var(--border)',
    borderRadius: 12,
  },

  skeleton: {
    background: 'linear-gradient(90deg, #f3efe9 0%, #faf5ed 50%, #f3efe9 100%)',
    borderRadius: 14,
    border: '1px solid var(--border)',
    animation: 'mqm-skel-pulse 1.4s ease-in-out infinite',
  },

  // Eficiencia por Metraje — calculator + table styles
  calcBody: { display: 'flex', flexDirection: 'column', gap: 14, padding: '16px 20px 18px' },
  calcInputs: { display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)', gap: 12, alignItems: 'end' },
  calcField: { display: 'flex', flexDirection: 'column', gap: 5 },
  calcLabel: { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.02em' },
  calcInput: {
    padding: '9px 12px', borderRadius: 8,
    border: '1px solid var(--border)', background: '#FDFBF8',
    fontSize: 13, fontFamily: "'DM Sans', sans-serif",
    color: 'var(--text-primary)', outline: 'none',
    width: '100%', boxSizing: 'border-box',
  },
  calcResults: {
    display: 'flex', flexDirection: 'column', gap: 8,
    padding: '14px 18px', borderRadius: 10,
    background: 'rgba(107,28,42,0.05)', border: '1px solid rgba(107,28,42,0.18)',
  },
  calcMain: { display: 'flex', flexDirection: 'column', gap: 2 },
  calcMainLabel: { fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' },
  calcMainValue: { fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 600, color: 'var(--wine)' },
  calcSplit: { display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text-secondary)' },
  calcNote: { fontSize: 11, color: 'var(--text-light)', fontStyle: 'italic' },
  calcEmpty: {
    padding: '14px 18px', borderRadius: 10,
    background: '#FDFBF8', border: '1px dashed var(--border)',
    fontSize: 12, color: 'var(--text-light)',
  },
  eficienciaScroll: { maxHeight: 400, overflow: 'auto' },
  eficienciaNote: {
    margin: '12px 0 0', fontSize: 11.5, lineHeight: 1.5,
    color: 'var(--text-secondary)',
  },
  // ── Compact / mobile additions (only merged when useBreakpoint() says so) ──
  containerTablet: { padding: '24px 20px', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' },
  cardHeaderMobile: { flexWrap: 'wrap', gap: 8, padding: '12px 14px' },
  calcBodyMobile: { padding: '14px 14px 16px' },
  calcInputsMobile: { gridTemplateColumns: 'minmax(0, 1fr)' },
  calcNoteMobile: { fontSize: 13, lineHeight: 1.45 },
  controlTouch: { minHeight: TAP_TARGET, boxSizing: 'border-box' },
  calcToggleBtnTouch: { ...tap, flex: 1 },
  eficienciaNoteCompact: { padding: '0 14px 14px', fontSize: 13 },
  kpiCardMobile: { padding: '12px 12px', minWidth: 0, overflow: 'hidden' },
  statValueMobile: { fontSize: 22, overflowWrap: 'anywhere' },
  metricTextMobile: { fontSize: 12 },
  closeBtnCompact: {
    ...tap,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    margin: -10, flexShrink: 0,
  },
  infoBtnCompact: {
    // 44px invisible hit area around the unchanged 18px "i" badge; the
    // negative margin keeps the surrounding layout as if it were 18px.
    ...tap,
    width: TAP_TARGET, height: TAP_TARGET,
    margin: -13,
    padding: 0, border: 'none', background: 'transparent',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer',
  },
  infoTipCompact: {
    position: 'fixed', top: 'auto',
    minWidth: 0, maxWidth: 'none',
    overflowY: 'auto', boxSizing: 'border-box',
    fontSize: 14, zIndex: 10060,
  },
  // Stacked "table row as card" pattern.
  mSortBar: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 14px', borderBottom: '1px solid var(--border)',
  },
  mRowCard: { padding: '12px 14px', borderTop: '1px solid #f3efe9' },
  mRowCardHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    gap: 10, marginBottom: 8,
  },
  mRowCardTitle: { fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', minWidth: 0, overflowWrap: 'anywhere' },
  mRowCardTag: { fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'nowrap', flexShrink: 0 },
  mRowCardNote: { fontSize: 13, color: 'var(--text-light)', fontStyle: 'italic', marginTop: 8, padding: '0 14px 12px' },
  mKvGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px 12px' },
  mKv: { display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 },
  mKvLabel: {
    fontSize: 11, fontWeight: 600, color: 'var(--text-light)',
    textTransform: 'uppercase', letterSpacing: '0.05em',
  },
  mKvValue: { fontSize: 15, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' },
  mDelayedCard: {
    display: 'block', width: '100%', textAlign: 'left',
    padding: '12px 14px', minHeight: TAP_TARGET,
    border: 'none', borderBottom: '1px solid #f3efe9', background: 'transparent',
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  mDelayedDays: {
    fontSize: 13, fontWeight: 700, color: '#B83B3B',
    background: 'rgba(184,59,59,0.08)', padding: '3px 10px', borderRadius: 16,
    whiteSpace: 'nowrap', flexShrink: 0,
  },
  mTeamHead: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, minWidth: 0 },
  mTeamDetail: {
    marginTop: 10, padding: '10px 12px', borderRadius: 8,
    background: 'var(--beige, #F7F3EE)', fontSize: 14,
  },
  origenUnifiedBodyCompact: { gridTemplateColumns: 'minmax(0, 1fr)', minHeight: 0 },
  origenChartColCompact: { borderRight: 'none', borderBottom: '1px solid #f3efe9', padding: '8px 12px', minWidth: 0 },
  origenListColCompact: { maxHeight: 'none', overflowY: 'visible', padding: '6px 0' },
  origenChannelRowCompact: { minHeight: TAP_TARGET, boxSizing: 'border-box' },
  origenChannelNameMobile: { fontSize: 15 },
  origenExpandItemMobile: { fontSize: 15 },
  delayedGroupHeaderCompact: { minHeight: TAP_TARGET, gap: 10 },
  delayedTaskNameMobile: { fontSize: 15 },
  delayedTaskMetaMobile: { fontSize: 13 },
  sortGroupMobile: { display: 'flex', width: '100%' },
  sortBtnCompact: { minHeight: TAP_TARGET, fontSize: 14 },
  twoColMobile: { gridTemplateColumns: 'minmax(0, 1fr)' },
  listRowMobile: { fontSize: 15, padding: '12px 14px' },
};
